<script setup>
import {
  ref,
  onMounted,
  watch,
  defineProps,
  computed,
  onUnmounted,
  defineEmits,
  nextTick,
} from "vue";
import {
  fetchDatasetById,
  fetchTags,
  createListing,
  fetchDatasets,
  updateListing,
  deleteListing,
  fetchListingById,
  fetchDraftListing,
  createDraftListing,
  updateDraftListing,
  fetchDraftByListingId,
  deleteDraftListing,
} from "@/services/api";
import { useLayoutComponents } from "@/composables/layout.js";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "@/composables/use-toast";
import { useStorage } from "@/composables/use-storage";
import FilePreviewModal from "@/components/modals/file-preview-modal.vue";
import Multiselect from "@/components/forms/inputs/multiselect.vue";
import Toast from "@/components/toast.vue";
import Stepper from "@/components/Stepper.vue";
import Alert from "@/components/Alert.vue";
import MarkdownRenderer from "@/components/MarkdownRenderer.vue";
import ListingTypeSelection from "@/components/listing/ListingTypeSelection.vue";
import EvalPublishingModelSelection from "@/components/listing/EvalPublishingModelSelection.vue";
import MissingFieldsModal from "@/components/listing/MissingFieldsModal.vue";
import ImageCropperModal from "@/components/modals/ImageCropperModal.vue";
import Checkbox from "@/components/checkbox.vue";
import { createPublicationRequest } from "@/services/publicationRequestsService";
import { useNotifications } from "@/composables/use-notifications";
import Modal from "@/components/modals/BaseModal.vue";
import axios from "axios";
import ListingLicenseModal from "@/pages/listing/listingLicenseModal.vue";
import ShowListingLicenseModal from "@/pages/listing/showListingLicenseModal.vue";
import StripeButtonConnect from "@/pages/stripe/StripeButtonConnect.vue";
import { getActivePaymentMethods } from "@/services/paymentMethodsService";
import USDCLogo from "@/assets/images/usdc.png";
import StripeLogo from "@/assets/images/stripe.png";
import { fetchEvalTags } from "@/pages/listing/services/tagsService";

const { addToast } = useToast();

const { notifications } = useNotifications();

const { TextInput, InputError, ButtonLoading, SelectInput } =
  useLayoutComponents();

const route = useRoute();
const router = useRouter();

const {
  userInfo,
  saveListingTempData,
  getListingTempDataByDataset,
  removeListingTempData,
  setIsFileProcessingValue,
  getIsFileProcessingValue,
  hasFlag,
  saveUserInfo,
  getUserInfo,
} = useStorage();

const isAdmin = userInfo.value?.is_admin === true;

const requiresPublicationReview = computed(() => {
  const group = String(userInfo.value?.group || "").toLowerCase();
  return (
    group.includes("self-serve") ||
    group.includes("newuser") ||
    group.includes("new-user") ||
    group.includes("new user")
  );
});

const fileSchemaProcessedNotification = computed(() => {
  return notifications.value.find(
    (notification) => notification.type === "file.schema_processed"
  );
});

const hasSchemaProcessedForDataset = computed(() => {
  if (!selectedDataset.value || !fileSchemaProcessedNotification.value)
    return false;

  const notification = fileSchemaProcessedNotification.value;
  return (
    notification &&
    notification.metadata &&
    String(notification.metadata.dataset_id) === String(selectedDataset.value)
  );
});

const steps = computed(() => {
  if (isEvalDataset.value && route.params.datasetId) {
    return ["Dataset", "Publishing Model", "Listing Details"];
  }
  if (isEvalDataset.value) {
    return ["Publishing Model", "Listing Details"];
  }
  return ["Dataset", "Listing Type", "Listing Details"];
});

const currentStep = computed(() => {
  if (isEvalDataset.value && route.params.datasetId) {
    return showListingForm.value ? 3 : 2;
  }
  if (isEvalDataset.value) {
    return showListingForm.value ? 2 : 1;
  }

  if (route.name === "listing-create" && !selectedDataset.value) {
    return 1;
  }

  if (!showListingForm.value) {
    return 2;
  }
  return 3;
});

const listingType = ref("buy_now_bidding");
const buyNowPrice = ref("");
const minimumBid = ref("");
const evalPrice = ref("");
const evalDistributionMode = ref("commercial");
const evalLicenseType = ref("commercial");
const evalLicenseTypeStored = ref(false); // boolean: true = open_source, false = commercial
const isEvalDataset = ref(
  route.query.subpage === "new-eval" || route.query.isEvalDataset === "true"
);

const emit = defineEmits(["update:hasStripeAccount"]);

const props = defineProps({
  id: {
    type: [String, Number],
    default: null,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
});

const isEditing = computed(() => {
  return (
    props.isEditing || route.name === "listing-edit" || Boolean(route.params.id)
  );
});

const isAdminEdit = computed(() => isAdmin && isEditing.value);

const listingId = computed(() => {
  if (route.name === "listing-edit") {
    const tempListingId = localStorage.getItem("temp_editing_listing_id");
    if (tempListingId) {
      return tempListingId;
    }
  }

  return props.id || route.params.id;
});

const title = ref("");
const modality = ref("");
const domain = ref("");
const useCase = ref("");
const description = ref("");
const shortDescription = ref("");
const price = ref("");
const license = ref("psdla");
const tags = ref([]);
const recipientAddress = ref("");
const previewImage = ref(null);
const previewMode = ref("preview");
const selectedDataset = ref("");
const availableDatasets = ref([]);
const datasetsLoaded = ref(false);
const featured = ref(false);
const acceptCredits = ref(false);
const premiumListingVisibility = ref(false);
const isPublished = ref(!String(userInfo.value?.group || "").toLowerCase().includes("self-serve"));
const walletPaymentEnabled = ref(false);
const stripePaymentEnabled = ref(false);
const stripeAccountId = computed(() => {
  return (
    currentDatasetSellerInfo.value?.stripe_account_id ||
    listingData.value?.datasets?.[0]?.dataset?.seller?.stripe_account_id ||
    null
  );
});
const userStripeAccountId = computed(() => {
  return userInfo.value?.seller?.stripe_account_id || null;
});
const hasStripeAccount = computed(() => {
  if (isAdmin) {
    return stripeAccountId.value !== null && stripeAccountId.value !== "";
  }
  return userStripeAccountId.value !== null && userStripeAccountId.value !== "";
});
const imageUrl = ref("");
const listingFee = ref(0);

const schemaColumns = ref([{ name: "", description: "" }]);

const showSchemaModal = ref(false);
const fileInfo = ref({
  name: "",
  size: "",
  type: "",
  schema: [],
});

const isUploading = ref(false);
const uploadProgress = ref(0);
const hasUploadError = ref(false);
const isSubmitting = ref(false);
const isLoading = ref(true);

const errors = ref({
  title: "",
  modality: "",
  description: "",
  shortDescription: "",
  schema: "",
  price: "",
  minimumBid: "",
  license: "",
  domain: "",
  recipientAddress: "",
  previewImage: "",
  selectedDataset: "",
  paymentMethods: "",
});

const modalityOptions = ref([]);
const useCaseOptions = ref([]);


const domainOptions = ref([]);
const tagsLoaded = ref(false);

const premiumOptions = [
  {
    label: "Featured Placement",
    value: "featured_placement",
    description: "Display listing in featured section",
  },
  {
    label: "Priority Search",
    value: "priority_search",
    description: "Higher ranking in search results",
  },
  {
    label: "Extended Visibility",
    value: "extended_visibility",
    description: "Longer display duration",
  },
  {
    label: "Custom Branding",
    value: "custom_branding",
    description: "Add custom colors and logo",
  },
  {
    label: "Analytics Dashboard",
    value: "analytics_dashboard",
    description: "Detailed performance metrics",
  },
  {
    label: "Contact Priority",
    value: "contact_priority",
    description: "Priority customer support",
  },
];

const selectedPremiumOptions = ref([]);
const showPremiumDropdown = ref(false);

const skipDatasetSchemaLoad = ref(false);

const showAlert = ref(false);
const showEditAlert = ref(false);
const alertTitle = ref("");
const alertMessage = ref("");
const alertType = ref("info");
const alertShowPasswordInput = ref(false);
const listingDataToSubmit = ref(null);
const listingData = ref(null);
const showSellerChangeAlert = ref(false);
const pendingDatasetChange = ref(null);
const currentDatasetSellerId = ref(null);
const originalDatasetId = ref(null);
const currentDatasetSellerInfo = ref(null);

const showCropper = ref(false);
const imageSource = ref("");
const isExternalDataset = ref(false);
const paymentMethods = ref([]);
const availablePayments = ref([]);
const isDatasetFromRoute = computed(() => {
  return Boolean(route.params.datasetId);
});

const isDatasetSelectionDisabled = computed(() => {
  return !isEditing.value && isDatasetFromRoute.value;
});

const isOnlyEvalDataset = computed(() => {
  return isEvalDataset.value && !fileInfo.value.name;
});

const isCoreDatasetFieldsEnabled = computed(() => {
  return (
    isEvalDataset.value &&
    selectedDataset.value &&
    selectedDataset.value !== "" &&
    selectedDataset.value !== "null" &&
    selectedDataset.value !== "undefined" &&
    fileInfo.value?.name
  );
});

const isFileInfoEmpty = computed(() => {
  const hasValidName =
    fileInfo.value?.name && fileInfo.value?.name !== "No dataset selected";

  if (isEvalDataset.value) {
    return !hasValidName || !fileInfo.value?.type;
  }

  return !hasValidName;
});

watch(isFileInfoEmpty, (isEmpty) => {
  if (isEmpty && isEvalDataset.value) {
    price.value = "";
    minimumBid.value = "";
  }
});

const checkFileProcessingStatus = async (datasetId) => {
  try {
    const dataset = await fetchDatasetById(datasetId);
    if (
      !dataset ||
      (!dataset.file_name &&
        !dataset.external_source_id &&
        !isOnlyEvalDataset.value)
    ) {
      return { isComplete: false, dataset: null };
    }
    if (dataset.external_source_id !== null) {
      isExternalDataset.value = true;
    }
    if (!availableDatasets.value.length) {
      availableDatasets.value = [
        {
          label: dataset.name || `Dataset #${dataset.id}`,
          value: dataset.id.toString(),
        },
      ];
      datasetsLoaded.value = true;
    }

    // For eval datasets, they are considered complete if they have eval_id
    // For external datasets, they are considered complete if they have external_source_id
    // For regular datasets, they need file_status === true
    let isComplete = false;
    if (dataset.eval_id !== null) {
      isComplete = true; // Eval datasets don't need file processing
    } else if (dataset.external_source_id !== null) {
      isComplete = true; // External datasets don't need file processing
    } else {
      isComplete = dataset.file_status === true; // Regular datasets need file processing
    }

    return {
      isComplete,
      dataset,
    };
  } catch (error) {
    console.error("Error checking file status", error);
    addToast("Error checking file status", "error");
    return { isComplete: false, dataset: null };
  }
};

const fileProcessingInterval = ref(null);
const hasPendingListingData = ref(false);
const pendingListingDatasetId = ref(null);
const isTempListingSaved = ref(false);

const startFileProcessingCheck = async (datasetId) => {
  if (!datasetId || isEditing.value) return;

  // Clear any existing interval to prevent multiple intervals
  if (fileProcessingInterval.value) {
    clearInterval(fileProcessingInterval.value);
    fileProcessingInterval.value = null;
  }

  setIsFileProcessingValue(true);

  const checkStatus = async () => {
    try {
      const { isComplete, dataset } =
        await checkFileProcessingStatus(datasetId);

      if (isComplete && dataset) {
        // Clear interval and stop processing
        clearInterval(fileProcessingInterval.value);
        fileProcessingInterval.value = null;
        setIsFileProcessingValue(false);

        if (dataset.external_source_id !== null) {
          return;
        }

        const info = buildFileInfo(dataset);
        if (info) {
          fileInfo.value = info;
          schemaColumns.value = info.schema;
        }
        return;
      }
    } catch (error) {
      console.error("Error checking file processing status", error);
      addToast("Error checking file processing status", "error");
      setIsFileProcessingValue(false);
      clearInterval(fileProcessingInterval.value);
      fileProcessingInterval.value = null;
    }
  };

  // First check immediately
  await checkStatus();

  // Only start interval if processing is still needed (check again after the immediate check)
  if (getIsFileProcessingValue()) {
    fileProcessingInterval.value = setInterval(checkStatus, 15000);
  }
};

const isFileProcessing = computed(() => {
  return getIsFileProcessingValue();
});

const datasetInfo = ref(null);

const loadDatasets = async (force = false) => {
  if (datasetsLoaded.value && !force) {
    return;
  }

  if (isDatasetFromRoute.value) {
    const dataset = await fetchDatasetById(route.params.datasetId);
    datasetInfo.value = dataset;
    if (dataset) {
      availableDatasets.value = [
        {
          label: dataset.name || `Dataset #${dataset.id}`,
          value: dataset.id.toString(),
        },
      ];
      datasetsLoaded.value = true;
    } else {
      availableDatasets.value = [];
    }
    return;
  }
  const type = isEvalDataset.value ? "eval" : "not_eval";
  const datasets = await fetchDatasets(0, 1000, type);
  if (Array.isArray(datasets) && datasets.length > 0) {
    availableDatasets.value = datasets.map((dataset) => ({
      label: dataset.name || `Dataset #${dataset.id}`,
      value: dataset.id.toString(),
    }));
    datasetsLoaded.value = true;
  } else {
    availableDatasets.value = [];
  }
};

const getSchemaFields = (schema) => {
  if (!schema) return [];
  if (schema.fields) return schema.fields;
  if (Array.isArray(schema)) return schema;
  return [];
};

const buildFileInfo = (dataset) => {
  if (!dataset) return null;

  return {
    name: dataset.file_name,
    size: dataset.file_size ? formatFileSize(dataset.file_size) : null,
    type: dataset.file_extension || null,
    schema: getSchemaFields(dataset.schema),
  };
};

const loadDatasetSchema = async (datasetId) => {
  if (!datasetId) return;

  // For eval datasets, skip file processing check
  const dataset = await fetchDatasetById(datasetId);

  if (
    isEditing.value &&
    dataset.seller?.user?.id &&
    dataset.seller?.user?.id !== currentDatasetSellerId.value &&
    isAdmin
  ) {
    showSellerChangeAlert.value = true;
    handleShowSellerChangeAlert(dataset);
  } else {
    showSellerChangeAlert.value = false;
  }

  if (!dataset) return;

  // Set current seller ID and seller info
  currentDatasetSellerId.value =
    dataset?.seller?.user?.id || dataset?.seller?.user_id || null;
  currentDatasetSellerInfo.value = dataset?.seller || null;

  if (dataset.external_source_id !== null) {
    isExternalDataset.value = true;
    return;
  }

  if (dataset.eval_id !== null) {
    isEvalDataset.value = true;
    // For eval datasets, set file info directly without processing check
    const info = buildFileInfo(dataset);
    if (info) {
      fileInfo.value = info;
      schemaColumns.value = info.schema;
    }
    return;
  } else {
    isEvalDataset.value = false;
    evalPrice.value = "";
  }

  isExternalDataset.value = false;

  // Only start file processing check for regular datasets
  await startFileProcessingCheck(datasetId);

  const info = buildFileInfo(dataset);
  if (info) {
    fileInfo.value = info;
    schemaColumns.value = info.schema;
  } else {
    resetSchemaInfo();
  }
};

const handleShowSellerChangeAlert = (dataset) => {
  // Store the current dataset ID for a potential dataset seller change
  const selectedOwner =
    dataset?.seller?.user?.username ||
    dataset?.seller?.user?.name ||
    "Unknown Seller";
  const originalOwner =
    listingData.value?.datasets?.[0]?.dataset?.seller?.user?.username ||
    listingData.value?.datasets?.[0]?.dataset?.seller?.user?.name ||
    "Unknown Seller";
  pendingDatasetChange.value = selectedDataset.value;
  alertTitle.value = "Dataset Seller Change Warning";
  alertMessage.value =
    "Changing from " +
    (listingData.value?.datasets?.[0]?.dataset?.name || "the current Dataset") +
    " (" +
    originalOwner +
    ") to " +
    (dataset?.name || "the new Dataset") +
    " (" +
    selectedOwner +
    ") will update the listing owner and may affect payment options. Do you want to continue?";
  alertType.value = "warning";

  // Set flag to prevent loadDatasetSchema from being called when reverting
  skipDatasetSchemaLoad.value = true;

  // Revert to original dataset
  selectedDataset.value = String(originalDatasetId.value);

  // Manually set seller ID back to original to prevent recursive updates
  if (listingData.value?.datasets?.[0]?.dataset?.seller?.user?.id) {
    currentDatasetSellerId.value =
      listingData.value.datasets[0].dataset.seller.user.id;
    currentDatasetSellerInfo.value =
      listingData.value.datasets[0].dataset.seller;
  }
};

const resetSchemaInfo = () => {
  fileInfo.value = {
    name: "No dataset selected",
    size: "0 Bytes",
    type: "",
    schema: [],
  };
  schemaColumns.value = [];
  isExternalDataset.value = false;
  showSellerChangeAlert.value = false;
  currentDatasetSellerInfo.value = null;
  currentDatasetSellerId.value = null;
};

watch(selectedDataset, async (newValue) => {
  if (skipDatasetSchemaLoad.value) {
    skipDatasetSchemaLoad.value = false;
    return;
  }

  if (!newValue) {
    resetSchemaInfo();
    return;
  }

  if (isEditing.value) {
    await loadDatasetSchema(newValue);
    return;
  }

  if (isDatasetFromRoute.value) {
    await startFileProcessingCheck(newValue);
    return;
  }

  await loadDatasetSchema(newValue);

  checkForPendingListing();
});

watch(hasSchemaProcessedForDataset, async (newValue) => {
  if (
    newValue &&
    hasPendingListingData.value &&
    selectedDataset.value &&
    !isFileProcessing.value
  ) {
    await completeSavedListing();
  }
});

// Additional watcher for when file processing completes
watch(isFileProcessing, async (newValue, oldValue) => {
  // If file processing just completed and we have pending data, try to complete the listing
  if (
    oldValue === true &&
    newValue === false &&
    hasPendingListingData.value &&
    selectedDataset.value
  ) {
    await completeSavedListing();
  }
});

const loadTags = async () => {
  if (tagsLoaded.value) {
    return;
  }
  const tagsFromApi = await fetchTags();

  if (Array.isArray(tagsFromApi)) {
    const domainTags = tagsFromApi
      .map((tag) => {
        if (typeof tag === "string") {
          return { label: tag, value: tag.toLowerCase() };
        } else if (tag && typeof tag === "object") {
          const tagType = tag.tag_type?.type?.toLowerCase();
          const isDomain = tagType === "domain";

          const { label, value } = {
            label: isDomain ? tag.name : "",
            value: isDomain
              ? String(tag.id) ||
                tag.tag_type?.value ||
                tag.tag_type?.name?.toLowerCase() ||
                ""
              : "",
          };

          if (label !== "" && value !== "") {
            return { label, value };
          }
        }
        return null;
      })
      .filter(Boolean);

    const modalityTags = tagsFromApi
      .map((tag) => {
        if (typeof tag === "string") {
          return { label: tag, value: tag.toLowerCase() };
        } else if (tag && typeof tag === "object") {
          const tagType = tag.tag_type?.type?.toLowerCase();
          const isModality = tagType === "modality";

          const { label, value } = {
            label: isModality ? tag.name : "",
            value: isModality
              ? String(tag.id) ||
                tag.tag_type?.value ||
                tag.tag_type?.name?.toLowerCase() ||
                ""
              : "",
          };

          if (label !== "" && value !== "") {
            return { label, value };
          }
        }
        return null;
      })
      .filter(Boolean);

    const useCaseTags = tagsFromApi
      .map((tag) => {
        if (typeof tag === "string") {
          return { label: tag, value: tag.toLowerCase() };
        } else if (tag && typeof tag === "object") {
          const tagType = tag.tag_type?.type?.toLowerCase();
          const isNotModalityOrDomain =
            tagType && tagType !== "modality" && tagType !== "domain";

          const { label, value } = {
            label: isNotModalityOrDomain ? tag.name : "",
            value: isNotModalityOrDomain
              ? String(tag.id) ||
                tag.tag_type?.value ||
                tag.tag_type?.name?.toLowerCase() ||
                ""
              : "",
          };

          if (label !== "" && value !== "") {
            return { label, value };
          }
        }
        return null;
      })
      .filter(Boolean);
    modalityOptions.value = modalityTags;
    domainOptions.value = domainTags;
    useCaseOptions.value = useCaseTags;
    tagsLoaded.value = true;
  } else {
    domainOptions.value = [];
    modalityOptions.value = [];
    useCaseOptions.value = [];
  }
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const togglePremiumOption = (optionValue) => {
  const index = selectedPremiumOptions.value.indexOf(optionValue);
  if (index > -1) {
    selectedPremiumOptions.value.splice(index, 1);
  } else {
    selectedPremiumOptions.value.push(optionValue);
  }
};

const togglePremiumDropdown = () => {
  showPremiumDropdown.value = !showPremiumDropdown.value;
};

const removeSelectedImage = () => {
  previewImage.value = null;
  uploadProgress.value = 0;
  hasUploadError.value = false;
  errors.value.previewImage = "";

  const fileInput = document.getElementById("imageInput");
  if (fileInput) {
    fileInput.value = "";
  }
};

const generatePreviewImage = () => {
  addToast("Coming soon!", "info");
};

const validateEthereumAddress = (address) => {
  const ethereumAddressRegex = /^0x[a-fA-F0-9]{40}$/;
  return ethereumAddressRegex.test(address);
};

const validateBasicFields = () => {
  const missingFields = [];

  if (!title.value) {
    errors.value.title = "Title is required";
    missingFields.push({
      label: "Title",
      description: "Enter a title for your listing",
    });
  }

  if (isPublished.value) {
    if (
      !modality.value ||
      (Array.isArray(modality.value) && modality.value.length === 0)
    ) {
      errors.value.modality = "Modality is required";
      missingFields.push({
        label: "Modality",
        description: "Select a modality for your listing",
      });
    }

    if (!description.value) {
      errors.value.description = "Description is required";
      missingFields.push({
        label: "Description",
        description: "Use markdown to format your description",
      });
    }

    if (!shortDescription.value) {
      errors.value.shortDescription = "Short description is required";
      missingFields.push({
        label: "Short Description",
        description:
          "Add a short description for your listing (max 300 characters)",
      });
    } else if (shortDescription.value.length > 300) {
      errors.value.shortDescription =
        "Short description must not exceed 300 characters";
      missingFields.push({
        label: "Short Description",
        description:
          "Add a short description for your listing (max 300 characters)",
      });
    }
  }

  return missingFields;
};

const addFieldError = (field, errorKey, label, description, missingFields) => {
  errors.value[errorKey] = `${label} is required`;
  missingFields.push({ label, description });
};

const validateStandardPriceFields = (missingFields) => {
  // skip validation for eval
  if (isEvalDataset.value) {
    return;
  }
  // validate based on listing type
  if (
    listingType.value === "buy_now" ||
    listingType.value === "buy_now_bidding"
  ) {
    if (!price.value || parseFloat(price.value) <= 0) {
      addFieldError(
        price,
        "price",
        "Price",
        "Enter a valid price greater than zero for your listing",
        missingFields
      );
    }
  }

  if (
    listingType.value === "bidding" ||
    listingType.value === "buy_now_bidding"
  ) {
    if (!minimumBid.value || parseFloat(minimumBid.value) <= 0) {
      addFieldError(
        minimumBid,
        "minimumBid",
        "Minimum Bid",
        "Enter a valid minimum bid greater than zero for your listing",
        missingFields
      );
    }
  }
};

const validateEvalPriceFields = (missingFields) => {
  if (!evalLicenseTypeStored.value && !evalPrice.value) {
    addFieldError(
      evalPrice,
      "evalPrice",
      "Eval Price",
      "Enter a eval price for your listing",
      missingFields
    );
  }

  // Validate core dataset fields if a core dataset is selected (skip when open source - price is sent as 0)
  if (isCoreDatasetFieldsEnabled.value && !evalLicenseTypeStored.value) {
    const hasValidPrice = price.value && parseFloat(price.value) > 0;
    const hasValidMinimumBid =
      minimumBid.value && parseFloat(minimumBid.value) > 0;

    if (!hasValidPrice && !hasValidMinimumBid) {
      addFieldError(
        price,
        "coreDatasetPrice",
        "Core Dataset Price Fields",
        "Enter at least one valid price field (Core Dataset Price or Core Dataset Minimum Bid).",
        missingFields
      );
    }
  }
};

const validatePriceFields = () => {
  const missingFields = [];

  if (isEvalDataset.value) {
    validateEvalPriceFields(missingFields);
  } else {
    validateStandardPriceFields(missingFields);
  }

  return missingFields;
};

const validateAdditionalFields = () => {
  const missingFields = [];
  if (!isPublished.value) return missingFields;

  validateDatasetField(missingFields);
  validateLicenseField(missingFields);
  validatePaymentFields(missingFields);
  validatePreviewImageField(missingFields);
  validateLicenseDataField(missingFields);

  return missingFields;
};

const validateDatasetField = (missingFields) => {
  if (selectedDataset.value) {
    return;
  }

  errors.value.selectedDataset = "Dataset is required";
  missingFields.push({
    label: "Dataset Selection",
    description: "Select a dataset for your listing",
  });
};

const validateLicenseField = (missingFields) => {
  if (license.value) {
    return;
  }

  errors.value.license = "License is required";
  missingFields.push({
    label: "License",
    description: "Select a license for your listing",
  });
};

const validatePaymentFields = (missingFields) => {
  const isEvalOpenSource = isEvalDataset.value && evalLicenseTypeStored.value;

  if (isEvalOpenSource) {
    errors.value.paymentMethods = "";
    errors.value.recipientAddress = "";
    return;
  }

  if (walletPaymentEnabled.value) {
    validateRecipientAddress(missingFields);
  }

  validatePaymentMethods(missingFields);
};

const validateRecipientAddress = (missingFields) => {
  if (!recipientAddress.value) {
    errors.value.recipientAddress =
      "Recipient address is required when wallet payment is enabled";
    missingFields.push({
      label: "Recipient Address",
      description: "Enter wallet address for payments",
    });
    missingFields.push({
      label: "Payment Methods",
      description: "Enable at least one payment method",
    });
    return;
  }

  if (!validateEthereumAddress(recipientAddress.value)) {
    errors.value.recipientAddress =
      "Invalid recipient address format (must start with 0x followed by 40 hexadecimal characters)";
    addToast(
      "Invalid recipient address format (must start with 0x followed by 40 hexadecimal characters)",
      "error"
    );
    missingFields.push({
      label: "Recipient Address",
      description: "Enter wallet address for payments",
    });
    missingFields.push({
      label: "Payment Methods",
      description: "Enable at least one payment method",
    });
  }
};

const validatePaymentMethods = (missingFields) => {
  if (!walletPaymentEnabled.value && !stripePaymentEnabled.value) {
    errors.value.paymentMethods =
      "Please enable at least one payment method (Wallet or Stripe)";
    addToast(
      "Please enable at least one payment method (Wallet or Stripe)",
      "error"
    );
    missingFields.push({
      label: "Payment Methods",
      description: "Enable at least one payment method",
    });
    return;
  }

  errors.value.paymentMethods = "";
};

const validatePreviewImageField = (missingFields) => {
  if (previewImage.value && previewImage.value.base64) {
    return;
  }

  errors.value.previewImage = "Preview image is required";
  missingFields.push({
    label: "Preview Image",
    description: "Upload a preview image for your listing",
  });
};

const validateLicenseDataField = (missingFields) => {
  if (licenseData.value) {
    return;
  }

  errors.value.license = "License is required";
  missingFields.push({
    label: "License",
    description: "Select a license for your listing",
  });
};

const validateForm = () => {
  errors.value = {
    title: "",
    modality: "",
    description: "",
    shortDescription: "",
    schema: "",
    price: "",
    minimumBid: "",
    license: "",
    domain: "",
    useCase: "",
    recipientAddress: "",
    previewImage: "",
    selectedDataset: "",
    premiumOptions: "",
    paymentMethods: "",
  };

  const missingFields = [
    ...validateBasicFields(),
    ...validatePriceFields(),
    ...validateAdditionalFields(),
  ];

  if (missingFields.length > 0) {
    showMissingFieldsModal.value = true;
    missingFieldsModalData.value = missingFields;
  }

  return missingFields.length === 0;
};

const handleSaveSchema = (updatedFileInfo) => {
  fileInfo.value = updatedFileInfo;
  schemaColumns.value = updatedFileInfo.schema || [];
  showSchemaModal.value = false;
};

const loadListingData = async (id) => {
  listingData.value = isDraft.value
    ? await fetchDraftListing(id)
    : await fetchListingById(id);
  await setInfos(listingData.value);

  if (!listingData.value.user_id && hasFlag("draft-feature")) {
    draftData.value = await fetchDraftByListingId(listingData.value.id);
    if (draftData.value) {
      showHasDraftModal.value = true;
    }
  } else {
    await formatLastDraftTime(listingData.value.updated_at);
    listingFee.value = listingData.value.fee
      ? Number(listingData.value.fee)
      : 0;
  }
};

const setInfos = async (listingData) => {
  setBasicListingInfo(listingData);
  await loadAndSetTags(listingData);

  if (isEvalDataset.value) {
    await loadEvalTagsFromListing(listingData);
  }

  setPreviewImage(listingData);
  setDatasetInfo(listingData);
  setListingLicenseInfo(listingData);
  setSchemaInfo(listingData);
};

const setListingLicenseInfo = (listingData) => {
  licenseData.value = {
    licenseId: listingData?.listingLicenses?.[0]?.license?.id,
    licenseName: listingData?.listingLicenses?.[0]?.license?.name,
    customClauses: listingData?.listingLicenses?.[0]?.custom_clauses,
    yearsDuration: listingData?.listingLicenses?.[0]?.years_duration,
    allowedAdditions:
      listingData?.listingLicenses?.[0]?.license?.allow_additions,
  };
};

const setBasicListingInfo = (listingData) => {
  title.value = listingData.name || "";
  description.value = listingData.description || "";
  shortDescription.value = listingData.short_description || "";
  tags.value = listingData?.tags?.map((tag) => String(tag?.id)) || [];
  price.value = listingData.price ? String(listingData.price) : "";
  minimumBid.value = listingData.minimum_bid
    ? String(listingData.minimum_bid)
    : "";
  evalPrice.value = listingData.eval_price
    ? String(listingData.eval_price)
    : "";
  evalLicenseTypeStored.value = listingData.is_open_source;
  license.value = listingData.data_license || "";
  recipientAddress.value = listingData.receipt_address || "";
  acceptCredits.value = listingData.accept_credits > 0;
  featured.value = listingData.featured > 0;
  premiumListingVisibility.value = listingData.premium > 0;
  isPublished.value =
    listingData.status === "active" || listingData.status !== "inactive";
  paymentMethods.value = listingData.payment_methods || [];
  const hasEvalId = !!listingData.datasets?.[0]?.dataset?.eval_id;
  const isEvalListing =
    listingData.type === "eval" ||
    hasEvalId ||
    (listingData.eval_price != null && listingData.eval_price !== "");
  listingType.value = isEvalListing ? "eval" : listingData.type || "";

  if (listingType.value === "eval" || isEvalDataset.value) {
    const licenseType = listingData.is_open_source;
    if (typeof licenseType === "boolean") {
      evalLicenseType.value = licenseType ? "open_source" : "commercial";
    } else {
      evalLicenseType.value = licenseType || "commercial";
    }
  }

  if (isEvalListing) {
    isEvalDataset.value = true;
  }

  if (
    listingData.payment_methods &&
    Array.isArray(listingData.payment_methods)
  ) {
    setPaymentMethods(listingData.payment_methods);
  }
};

const setPaymentMethods = (listingPaymentMethods) => {
  if (
    !Array.isArray(listingPaymentMethods) ||
    !availablePayments.value.length
  ) {
    return;
  }

  const walletId = availablePayments.value.find((p) => p.name === "wallet")?.id;
  const stripeId = availablePayments.value.find((p) => p.name === "stripe")?.id;
  const creditsId = availablePayments.value.find(
    (p) => p.name === "credits"
  )?.id;

  walletPaymentEnabled.value = listingPaymentMethods.includes(walletId);
  stripePaymentEnabled.value = listingPaymentMethods.includes(stripeId);
  acceptCredits.value = listingPaymentMethods.includes(creditsId);

  paymentMethods.value = [...listingPaymentMethods];
};

const loadAndSetTags = async (listingData) => {
  if (!tagsLoaded.value) {
    await loadTags();
  }

  tags.value = [];
  domain.value = [];
  modality.value = [];
  useCase.value = [];
  if (listingData.tags && Array.isArray(listingData.tags)) {
    if (
      listingData.tags.length > 0 &&
      typeof listingData.tags[0] === "number"
    ) {
      tags.value = listingData.tags.map((tagId) => String(tagId));
    } else {
      tags.value = listingData?.tags?.map((tag) => String(tag?.id)) || [];
    }
  } else if (
    listingData.listing_tags &&
    Array.isArray(listingData.listing_tags)
  ) {
    tags.value = listingData.listing_tags.map((tag) =>
      tag.tag_id !== undefined ? String(tag.tag_id) : String(tag.id)
    );
  }
  const domainTagIds = domainOptions.value.map((option) => option.value);
  const modalityTagIds = modalityOptions.value.map((option) => option.value);
  const useCaseTagIds = useCaseOptions.value.map((option) => option.value);
  domain.value = tags.value.filter((tagId) => domainTagIds.includes(tagId));
  modality.value = tags.value.filter((tagId) => modalityTagIds.includes(tagId));
  useCase.value = tags.value.filter((tagId) => useCaseTagIds.includes(tagId));
};

const setPreviewImage = (listingData) => {
  if (listingData.preview_image) {
    previewImage.value = {
      base64: listingData.preview_image,
      name: "Current Image",
      isUrl: listingData.preview_image.startsWith("http"),
    };
  }
};

const extractDatasetId = (datasetObject) => {
  if (typeof datasetObject !== "object") {
    return datasetObject;
  }

  if (datasetObject.dataset_id) {
    return datasetObject.dataset_id;
  }

  if (datasetObject.id) {
    return datasetObject.id;
  }

  if (datasetObject.dataset && datasetObject.dataset.id) {
    return datasetObject.dataset.id;
  }

  return null;
};

const extractDatasetDetails = (listingData) => {
  const hasDatasets =
    listingData.datasets &&
    Array.isArray(listingData.datasets) &&
    listingData.datasets.length > 0;

  if (!hasDatasets && !listingData.dataset_id) {
    return null;
  }

  let datasetId;
  let datasetInfo = null;

  if (listingData.dataset_id) {
    datasetId = listingData.dataset_id;
  } else if (hasDatasets) {
    const datasetObject = listingData.datasets[0];
    datasetId = extractDatasetId(datasetObject);
    datasetInfo = datasetObject.dataset || datasetObject;
  }

  return { datasetId, datasetInfo };
};

const setSelectedDataset = (datasetId, datasetInfo) => {
  if (!datasetId) {
    return;
  }

  skipDatasetSchemaLoad.value = true;
  selectedDataset.value = String(datasetId);
  currentDatasetSellerId.value = datasetInfo?.seller?.user?.id || null;
  currentDatasetSellerInfo.value = datasetInfo?.seller || null;

  const datasetExists = availableDatasets.value.some(
    (d) => d.value === String(datasetId)
  );
  if (!datasetExists) {
    availableDatasets.value.push({
      label: datasetInfo?.name || `Dataset #${datasetId}`,
      value: String(datasetId),
    });
  }
};

const setFileInfoFromDataset = (listingData, datasetInfo) => {
  if (!datasetInfo) {
    return;
  }

  if (datasetInfo.external_source) {
    isExternalDataset.value = true;
    return;
  }

  const schema = Array.isArray(listingData.schema) ? listingData.schema : [];
  const fileName = datasetInfo.file_name ? datasetInfo.file_name : null;
  const fileExtension = datasetInfo.file_extension || "";

  let fullFileName = "";
  if (fileName) {
    if (fileName.endsWith(`.${fileExtension}`) || !fileExtension) {
      fullFileName = fileName;
    } else {
      fullFileName = `${fileName}.${fileExtension}`;
    }
  }

  fileInfo.value = {
    name: fullFileName,
    size: datasetInfo.file_size
      ? formatFileSize(Number(datasetInfo.file_size))
      : "Unknown",
    type: fileExtension,
    schema: schema,
  };

  schemaColumns.value = schema;

  if (isAdmin) {
    const dataset = availableDatasets.value.find(
      (dataset) => dataset.value === String(datasetInfo.id)
    );
    if (dataset) {
      const owner =
        datasetInfo.seller?.user?.username ||
        datasetInfo.seller?.user?.name ||
        "Unknown Seller";
      dataset.label = datasetInfo.name
        ? `${datasetInfo.name} (${owner})`
        : `Dataset #${datasetInfo.id} (${owner})`;
    }
  }
};

const setDatasetInfo = (listingData) => {
  const datasetDetails = extractDatasetDetails(listingData);
  if (!datasetDetails) {
    return;
  }

  const { datasetId, datasetInfo } = datasetDetails;

  if (isEditing.value) {
    originalDatasetId.value = datasetId;
    currentDatasetSellerInfo.value = datasetInfo?.seller || null;
  }

  setSelectedDataset(datasetId, datasetInfo);
  setFileInfoFromDataset(listingData, datasetInfo);
};

const setSchemaInfo = (listingData) => {
  if (!listingData.schema) {
    return;
  }

  let schemaFields = [];

  if (listingData.schema.fields && Array.isArray(listingData.schema.fields)) {
    schemaFields = listingData.schema.fields;
  } else if (Array.isArray(listingData.schema)) {
    schemaFields = listingData.schema;
  }

  schemaColumns.value = schemaFields;

  if (!fileInfo.value.schema || fileInfo.value.schema.length === 0) {
    fileInfo.value.schema = schemaFields;
  }
};

const formatSchema = () => {
  return {
    fields: Array.isArray(fileInfo.value.schema)
      ? fileInfo.value.schema.map((field) => ({
          name: field.name || "",
          type: field.type || "",
          description: field.description || "",
        }))
      : [],
  };
};

const saveListingTemporarily = () => {
  if (!selectedDataset.value && isPublished.value) {
    addToast("Please select a dataset first", "error");
    return false;
  }

  const listingData = prepareListingData();
  const success = saveListingTempData(selectedDataset.value, listingData);

  if (success) {
    hasPendingListingData.value = true;
    pendingListingDatasetId.value = selectedDataset.value;
    return true;
  } else {
    addToast("Failed to save listing data", "error");
    return false;
  }
};

const completeSavedListing = async () => {
  if (!selectedDataset.value) {
    return;
  }

  const savedData = getListingTempDataByDataset(selectedDataset.value);
  if (!savedData) {
    return;
  }

  try {
    await loadDatasetSchema(selectedDataset.value);

    if (savedData.schema.fields.length === 0 && !isExternalDataset.value) {
      resetSchemaInfo();
      return;
    }

    const completedData = {
      ...savedData,
      user_id: datasetInfo.value?.seller?.user_id,
      schema: formatSchema(),
    };

    const response = await createListing(completedData);

    if (response?.status === 200 || response?.status === 201) {
      removeListingTempData(selectedDataset.value);
      hasPendingListingData.value = false;
      pendingListingDatasetId.value = null;
      isTempListingSaved.value = false;
      hasSchemaProcessedForDataset.value = false;
      addToast("Listing published successfully!", "success");

      // Redirect to appropriate page after successful completion
      if (isEvalDataset.value) {
        router.push({
          name: "creator-profile",
          query: { page: "evals", subpage: "edit-eval" },
        });
      } else {
        router.push({ name: "creator-profile", query: { tab: "listings" } });
      }
    }
  } catch (error) {
    console.error("Error completing listing creation", error);
    addToast("Failed to complete listing creation", "error");
  }
};

const checkForPendingListing = () => {
  if (!selectedDataset.value) return;

  const savedData = getListingTempDataByDataset(selectedDataset.value);
  if (savedData) {
    hasPendingListingData.value = true;
    pendingListingDatasetId.value = selectedDataset.value;

    if (hasSchemaProcessedForDataset.value) {
      completeSavedListing();
    }
  }
};

const buildFileName = () => {
  const fileName = fileInfo.value.name || "";
  const fileExtension = fileInfo.value.type || "";
  return fileName.endsWith(`.${fileExtension}`) || !fileExtension
    ? fileName
    : `${fileName}.${fileExtension}`;
};

const combineTags = () => {
  const combined = [];

  if (isEvalDataset.value) {
    const evalTagIds = getEvalTagIds();
    combined.push(...evalTagIds);
  } else {
    if (Array.isArray(domain.value) && domain.value.length > 0) {
      combined.push(...domain.value);
    } else if (domain.value) {
      combined.push(domain.value);
    }

    if (Array.isArray(useCase.value) && useCase.value.length > 0) {
      combined.push(...useCase.value);
    } else if (useCase.value) {
      combined.push(useCase.value);
    }
  }

  if (Array.isArray(modality.value) && modality.value.length > 0) {
    combined.push(...modality.value);
  } else if (modality.value) {
    combined.push(modality.value);
  }

  return combined
    .map((tag) => Number.parseInt(tag))
    .filter((tagId) => !Number.isNaN(tagId))
    .map((tagId) => ({ tag_id: tagId }));
};

const createListingDetails = () => {
  const listingDetails = {
    ...buildBaseListingDetails(),
    ...buildPricingDetails(),
  };

  applyEvalListingFlags(listingDetails);
  applyEvalTagType(listingDetails);

  return listingDetails;
};

const buildBaseListingDetails = () => ({
  name: title.value,
  user_id: datasetInfo.value?.seller?.user_id,
  description: description.value,
  short_description: shortDescription.value,
  delivery_method: "dataset",
  consumption_url: "https://example.com/dataset/123",
  preview_image: getPreviewImage(),
  currency: "USDC",
  data_license: license.value,
  receipt_address: walletPaymentEnabled.value ? recipientAddress.value : null,
  file_name: buildFileName(),
  file_size: fileInfo.value.size,
  file_extension: fileInfo.value.type,
  schema: formatSchema(),
  listing_tags: combineTags(),
  featured: Number(featured.value),
  accept_credits: acceptCredits.value,
  premium: premiumListingVisibility.value,
  premium_options: selectedPremiumOptions.value,
  status:
    requiresPublicationReview.value
      ? "inactive"
      : isPublished.value
        ? "active"
        : "inactive",
  payment_methods: paymentMethods.value,
});

const buildPricingDetails = () => ({
  price: getListingPrice(),
  minimum_bid: Number(minimumBid.value),
  type: getListingType(),
  eval_price: getEvalPrice(),
  fee: getListingFee(),
});

const getPreviewImage = () => {
  const base64 = previewImage.value?.base64;
  if (!base64 || !base64.startsWith("data:image")) {
    return null;
  }

  return base64;
};

const isEvalListing = () =>
  isEvalDataset.value || parseFloat(evalPrice.value) > 0;

const hasEvalOpenSourceLicense = () =>
  isEvalListing() && Boolean(evalLicenseTypeStored.value);

const getListingPrice = () => {
  if (hasEvalOpenSourceLicense()) {
    return 0;
  }

  return Number(price.value);
};

const getListingType = () => (isEvalListing() ? "eval" : listingType.value);

const getEvalPrice = () => {
  if (hasEvalOpenSourceLicense()) {
    return 0;
  }

  if (evalPrice.value === "") {
    return null;
  }

  return Number(evalPrice.value);
};

const getListingFee = () => {
  if (listingFee.value === "") {
    return null;
  }

  return Number(listingFee.value);
};

const applyEvalListingFlags = (listingDetails) => {
  if (!isEvalListing()) {
    return;
  }

  listingDetails.is_open_source = evalLicenseTypeStored.value ?? false;
};

const applyEvalTagType = (listingDetails) => {
  if (!isEvalDataset.value || !evalTagTypeSelected.value) {
    return;
  }

  const tagTypeId = Number.parseInt(evalTagTypeSelected.value);
  if (!Number.isNaN(tagTypeId)) {
    listingDetails.tag_type_id = tagTypeId;
  }
};

const prepareListingData = () => {
  const listingDetails = createListingDetails();
  const datasetsList = selectedDataset.value
    ? [Number(selectedDataset.value)]
    : [];
  const listingLicense = {
    license_id: licenseData.value.licenseId,
    custom_clauses: licenseData.value.customClauses,
    years_duration: licenseData.value.yearsDuration,
  };

  const payload = isEditing.value
    ? {
        values: {
          listing: listingDetails,
          datasets: datasetsList,
          listing_license: listingLicense,
        },
      }
    : {
        listing: listingDetails,
        datasets: datasetsList,
        listing_license: listingLicense,
      };

  return payload;
};

const handleEditingMode = () => {
  showEditAlert.value = true;
  alertTitle.value = "Confirm Update";
  alertMessage.value = "Please enter your password to confirm listing update.";
  alertType.value = "warning";
};

const handleFileProcessingScenario = async () => {
  if (!validateForm()) return false;

  isSubmitting.value = true;
  try {
    const success = saveListingTemporarily();
    if (success && !isTempListingSaved.value && isPublished.value) {
      isTempListingSaved.value = true;
      addToast(
        "Your listing is saved and will be published automatically once file processing completes.",
        "success"
      );
    }
    return true;
  } finally {
    isSubmitting.value = false;
  }
};

const handleSuccessfulCreation = (response) => {
  if (!response || (response.status !== 200 && response.status !== 201)) return;

  if (showListingForm.value && !isEditing.value) {
    // Redirect to evals page for eval listings, otherwise to listings page
    if (isEvalDataset.value) {
      router.push("/creator-profile?page=evals&subpage=eval-listings");
    } else {
      router.push({ name: "creator-profile", query: { tab: "listings" } });
    }
  } else {
    router.back();
  }
};

const resolveListingIdFromResponse = (response) => {
  return (
    response?.data?.id ||
    response?.data?.listing?.id ||
    response?.data?.data?.id ||
    null
  );
};

const submitPublicationReviewIfNeeded = async (listingId) => {
  if (!requiresPublicationReview.value || !listingId) return false;
  try {
    const isOpenSource =
      typeof evalLicenseTypeStored.value === "boolean"
        ? evalLicenseTypeStored.value
        : evalLicenseType.value === "open_source";
    await createPublicationRequest({
      listing_id: Number(listingId),
      is_open_source: Boolean(isOpenSource),
      notes: "",
    });
    addToast("Listing submitted for publication review", "success");
    return true;
  } catch (error) {
    console.error("Error submitting publication request", error);
    addToast(
      error?.response?.data?.message ||
        "Listing saved as private. Failed to submit for review.",
      "error"
    );
    return false;
  }
};

const convertDraftToListingData = () => {
  const listingDetails = createListingDetails();
  const datasetsList = selectedDataset.value
    ? [Number(selectedDataset.value)]
    : [];

  if (draftData.value && draftData.value.listing_id) {
    return { values: { listing: listingDetails, datasets: datasetsList } };
  } else {
    return { listing: listingDetails, datasets: datasetsList };
  }
};

const handleDraftPublication = async () => {
  try {
    let response;
    const listingData = convertDraftToListingData();

    if (draftData.value && draftData.value.listing_id) {
      response = await updateListing(draftData.value.listing_id, listingData);
    } else {
      response = await createListing(listingData);
    }

    if (response && (response.status === 200 || response.status === 201)) {
      await deleteDraftListing(listingId.value);
      const createdId =
        resolveListingIdFromResponse(response) || draftData.value?.listing_id;
      if (requiresPublicationReview.value && isPublished.value && createdId) {
        await submitPublicationReviewIfNeeded(createdId);
      }
      handleSuccessfulCreation(response);
    }
  } catch (error) {
    console.error("Error publishing listing", error);
    addToast("Error publishing listing", "error");
  }
};

const showMissingFieldsModal = ref(false);
const missingFieldsModalData = ref([]);
const showStripeConnectModal = ref(false);
const stripeConnectionCheckInterval = ref(null);

const showOpenSourceWarningAlert = ref(false);
const openSourceWarningCheckboxAccepted = ref(false);
const openSourceWarningAlreadyConfirmed = ref(false);

const submitCreateListing = async () => {
  clearErrors();

  // Only consider file still processing if isFileProcessing is true
  // Don't use isSchemaReady as a condition since schema might be empty for valid datasets
  const isFileStillProcessing = isFileProcessing.value;
  const shouldSaveTemporarily =
    !isExternalDataset.value &&
    isFileStillProcessing &&
    !isEditing.value &&
    isPublished.value &&
    !isOnlyEvalDataset.value;

  if (
    stripePaymentEnabled.value &&
    !hasStripeAccount.value &&
    !walletPaymentEnabled.value
  ) {
    addToast(
      "Please connect your Stripe account to enable Stripe payments",
      "error"
    );
    return;
  }

  if (shouldSaveTemporarily) {
    await handleFileProcessingScenario();
    return;
  }
  if (!validateForm()) return;

  // Show open source warning only when pending items are done (validation passed) and creating an eval listing with Open Source (not when editing)
  const shouldShowOpenSourceWarning =
    isEvalDataset.value && evalLicenseTypeStored.value && !isEditing.value;
  if (shouldShowOpenSourceWarning && !openSourceWarningAlreadyConfirmed.value) {
    showOpenSourceWarningAlert.value = true;
    return;
  }
  if (shouldShowOpenSourceWarning && openSourceWarningAlreadyConfirmed.value) {
    openSourceWarningAlreadyConfirmed.value = false;
  }

  isSubmitting.value = true;

  try {
    const listingData = prepareListingData();
    listingDataToSubmit.value = listingData;
    alertShowPasswordInput.value = true;
    if (isEditing.value && !isDraft.value) {
      handleEditingMode();
    } else if (isDraft.value) {
      await handleDraftPublication();
    } else {
      const response = await createListing(listingData);
      if (response && (response.status === 200 || response.status === 201)) {
        const createdId = resolveListingIdFromResponse(response);
        if (requiresPublicationReview.value && isPublished.value && createdId) {
          await submitPublicationReviewIfNeeded(createdId);
        }
      }
      handleSuccessfulCreation(response);
    }
  } finally {
    isSubmitting.value = false;
  }
};

const confirmUpdateListing = async (data) => {
  if (!listingId.value || !listingDataToSubmit.value) return;

  isSubmitting.value = true;
  try {
    const payload = {
      ...listingDataToSubmit.value,
      password: data?.password || null,
    };

    const wantsReview =
      requiresPublicationReview.value &&
      isPublished.value &&
      listingData.value?.status === "inactive";

    const response = await updateListing(listingId.value, payload);

    if (response && (response.status === 200 || response.status === 201)) {
      if (wantsReview) {
        await submitPublicationReviewIfNeeded(listingId.value);
      }
      if (route.name === "listing-edit") {
        localStorage.removeItem("temp_editing_listing_id");
      }
      router.back();
    }
  } catch (error) {
    console.error("Error updating listing", error);
    addToast("Error updating listing", "error");
  } finally {
    showEditAlert.value = false;
    isSubmitting.value = false;
  }
};

const cancelAlert = () => {
  showAlert.value = false;
  showEditAlert.value = false;
  showOpenSourceWarningAlert.value = false;
  openSourceWarningCheckboxAccepted.value = false;
  listingDataToSubmit.value = null;
};

const handleCloseOpenSourceWarningAlert = () => {
  showOpenSourceWarningAlert.value = false;
  openSourceWarningCheckboxAccepted.value = false;
};

const handleConfirmOpenSourceWarning = () => {
  showOpenSourceWarningAlert.value = false;
  openSourceWarningCheckboxAccepted.value = false;
  openSourceWarningAlreadyConfirmed.value = true;
  submitCreateListing();
};

const confirmSellerChange = async () => {
  if (!isAdmin) {
    addToast("Unauthorized: Admin access required", "error");
    return;
  }

  if (pendingDatasetChange.value) {
    const datasetToLoad = pendingDatasetChange.value;

    pendingDatasetChange.value = null;
    showSellerChangeAlert.value = false;
    originalDatasetId.value = datasetToLoad;
    skipDatasetSchemaLoad.value = false;
    selectedDataset.value = String(datasetToLoad);

    await loadDatasetSchema(datasetToLoad);
  }
};

const cancelSellerChange = async () => {
  if (!isAdmin) {
    addToast("Unauthorized: Admin access required", "error");
    return;
  }
  pendingDatasetChange.value = null;
  showSellerChangeAlert.value = false;

  if (originalDatasetId.value) {
    skipDatasetSchemaLoad.value = true;
    selectedDataset.value = String(originalDatasetId.value);

    // Restore fileInfo from the original dataset
    let dataset = listingData.value?.datasets?.[0]?.dataset;

    if (!dataset) {
      try {
        dataset = await fetchDatasetById(originalDatasetId.value);
      } catch (error) {
        console.error("Error fetching dataset on cancel", error);
        addToast("Error fetching dataset on cancel", "error");
      }
    }

    // Restore seller info and fileInfo
    if (dataset) {
      if (dataset.seller?.user?.id) {
        currentDatasetSellerId.value = dataset.seller.user.id;
        currentDatasetSellerInfo.value = dataset.seller;
      }

      const info = buildFileInfo(dataset);
      if (info) {
        fileInfo.value = info;
        schemaColumns.value = info.schema;
      }
    }

    setTimeout(() => {
      isFileProcessing.value = false;
      skipDatasetSchemaLoad.value = false;
    }, 100);
  }
};

const pageTitle = computed(() => {
  const createText = isEvalDataset.value ? "New Eval Listing" : "New Listing";
  const editText = isEvalDataset.value ? "Edit Eval Listing" : "Edit Listing";
  return isEditing.value ? editText : createText;
});

const buttonText = computed(() => {
  if (isDraft.value) {
    return requiresPublicationReview.value && isPublished.value
      ? "Submit for Review"
      : "Publish Listing";
  }
  if (isEditing.value) {
    return requiresPublicationReview.value &&
      isPublished.value &&
      listingData.value?.status === "inactive"
      ? "Submit for Review"
      : "Update Listing";
  }

  const isSchemaReady =
    fileInfo.value.schema && fileInfo.value.schema.length > 0;
  const isFileStillProcessing = isFileProcessing.value || !isSchemaReady;

  if (isFileStillProcessing) {
    return hasPendingListingData.value ? "Listing Saved" : "Save Listing";
  }

  if (requiresPublicationReview.value && isPublished.value) {
    return "Submit for Review";
  }

  return "Create Listing";
});

const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    if (!file.type.startsWith("image/")) {
      errors.value.previewImage = "Please select an image file";
      hasUploadError.value = true;
      addToast("Please select a valid image file", "error");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      imageSource.value = e.target.result;
      showCropper.value = true;
    };
    reader.readAsDataURL(file);
  }
};

const handleCroppedImageSave = (image) => {
  if (!image || !image.base64) return;

  previewImage.value = {
    file: null,
    name: image.name || "cropped-image.jpg",
    base64: image.base64,
  };

  isUploading.value = false;
  uploadProgress.value = 100;
  hasUploadError.value = false;
  errors.value.previewImage = "";

  const fileInput = document.getElementById("imageInput");
  if (fileInput) fileInput.value = "";
};

const cancelCropping = () => {
  showCropper.value = false;
  const fileInput = document.getElementById("imageInput");
  if (fileInput) fileInput.value = "";
};

const showListingForm = ref(isEditing.value);

const handleContinue = () => {
  if (
    listingType.value &&
    ((listingType.value === "buy_now" && buyNowPrice.value) ||
      (listingType.value === "bidding" && minimumBid.value) ||
      (listingType.value === "buy_now_bidding" &&
        buyNowPrice.value &&
        minimumBid.value))
  ) {
    if (listingType.value === "bidding") {
      price.value = "";
    } else {
      price.value = buyNowPrice.value;
    }
    showListingForm.value = true;
  }
};

const handleEvalPublishingContinue = () => {
  if (!evalDistributionMode.value) return;
  evalLicenseType.value = evalDistributionMode.value;
  showListingForm.value = true;
};

const handleBack = () => {
  showListingForm.value = false;
};

const processImageFile = async (file) => {
  if (!file || !file.type.startsWith("image/")) {
    errors.value.previewImage = "Please select an image file";
    hasUploadError.value = true;
    addToast("Please select a valid image file", "error");
    return;
  }

  const reader = new FileReader();
  reader.onload = async (e) => {
    try {
      const result = await axios.post(
        `/api/users/${userInfo.value.id}/upload-image`,
        e.target.result
      );
      if ((result.status === 200 || result.status === 201) && result.data) {
        imageUrl.value = result.data;
        if (description.value.includes("![Image](Uploading Image...)")) {
          description.value = description.value.replace(
            "![Image](Uploading Image...)",
            `![Image](${imageUrl.value})`
          );
        } else {
          description.value += `\n\n![Image](${imageUrl.value})`;
        }
        addToast("Image uploaded successfully", "success");
      }
    } catch (error) {
      console.error("Error uploading image", error);
      addToast("Failed to upload image", "error");
    }
  };
  reader.readAsDataURL(file);
};

const handlePaste = async (event) => {
  if (event.target?.files?.length > 0) {
    const file = event.target.files[0];
    description.value += `\n\n![Image](Uploading Image...)`;
    await processImageFile(file);
    return;
  }

  if (!event.clipboardData) return;

  if (
    event.clipboardData.types &&
    event.clipboardData.types.includes("text/plain")
  ) {
    return;
  }

  const items = event.clipboardData?.items;
  if (!items) return;

  description.value += `\n\n![Image](Uploading Image...)`;

  for (const item of items) {
    if (item.type.startsWith("image/")) {
      event.preventDefault();

      const file = item.getAsFile();
      if (file) {
        await processImageFile(file);
      }
      break;
    }
  }
};

const backRoute = () => {
  router.back();
};

const showDeleteModal = ref(false);

const handleShowDeleteModal = () => {
  showDeleteModal.value = true;
};

const handleCloseDeleteModal = () => {
  showDeleteModal.value = false;
};

const deleteAlertMessage = computed(() => {
  const base = `Are you sure you want to delete this ${isDraft.value ? "draft" : ""} listing? This action cannot be undone.`;
  const isEvalOpenSource =
    isEvalDataset.value && evalLicenseTypeStored.value && !isDraft.value;
  if (isEvalOpenSource) {
    return `${base}<br><br><strong>Note:</strong> Even after deleting the listing, the dataset information will still remain public in the repository.`;
  }
  return base;
});

const confirmDeleteListing = async (data) => {
  if (!listingId.value) return;
  isSubmitting.value = true;

  try {
    const response = isDraft.value
      ? await deleteDraftListing(listingId.value, data)
      : await deleteListing(listingId.value, data);
    if (response.status === 200 || response.status === 201) {
      showDeleteModal.value = false;
      router.back();
    }
    if (response.status === 409) {
      addToast(response.message, "error");
    }
  } catch (error) {
    console.error("Error deleting listing", error);
    addToast("Error deleting listing", "error");
  }
  isSubmitting.value = false;
};

// Draft
const isDraft = computed(() => {
  return localStorage.getItem("temp_editing_listing_is_draft") === "true";
});
const showHasDraftModal = ref(false);
const draftData = ref(null);

const autoSaveInterval = ref(null);
const hasAutoSaveDraft = ref(false);
const lastSavedData = ref(null);

const startAutoSave = () => {
  if (isEditing.value && !isDraft.value) return;
  if (autoSaveInterval.value) {
    clearInterval(autoSaveInterval.value);
  }
  const minutes = 3;
  autoSaveInterval.value = setInterval(autoSaveListingData, minutes * 60000);
};

const stopAutoSave = () => {
  if (autoSaveInterval.value) {
    clearInterval(autoSaveInterval.value);
    autoSaveInterval.value = null;
  }
};

const autoSaveListingData = async () => {
  const data = prepareDraftListingData();
  if (isDraft.value || hasAutoSaveDraft.value) {
    const currentListingId =
      listingId.value || localStorage.getItem("temp_editing_listing_id");
    if (currentListingId && checkFiledsChanges(data)) {
      const result = await updateDraftListing(currentListingId, data);
      if (result) {
        lastSavedData.value = JSON.parse(JSON.stringify(data));
        formatLastDraftTime(result.updated_at);
      }
    }
    return;
  }

  if (data.listing.name !== "") {
    const newDraft = await createDraftListing(data);
    const draftId = newDraft?.id || newDraft?.data?.id;
    if (newDraft && draftId) {
      localStorage.setItem("temp_editing_listing_id", draftId.toString());
      localStorage.setItem("temp_editing_listing_is_draft", "true");
      hasAutoSaveDraft.value = true;
      lastSavedData.value = JSON.parse(JSON.stringify(data));
      formatLastDraftTime(newDraft.updated_at);
    }
  }
};

const checkFiledsChanges = (data) => {
  if (!lastSavedData.value) {
    return true;
  }

  const currentListing = data.listing;
  const lastListing = lastSavedData.value.listing;

  const fieldsToCompare = [
    "name",
    "description",
    "short_description",
    "price",
    "minimum_bid",
    "eval_price",
    "data_license",
    "receipt_address",
    "delivery_method",
    "accept_credits",
    "featured",
    "premium",
    "status",
  ];

  for (const field of fieldsToCompare) {
    if (currentListing[field] !== lastListing[field]) {
      return true;
    }
  }

  const currentDatasetId = currentListing.dataset_id || null;
  const lastDatasetId = lastListing.dataset_id || null;

  if (currentDatasetId !== lastDatasetId) {
    return true;
  }

  const currentTags = currentListing.tags || [];
  const lastTags = lastListing.tags || [];

  return checkTagsArrayChanges(currentTags, lastTags);
};

const checkTagsArrayChanges = (currentTags, lastTags) => {
  const currentTagIds = currentTags
    .filter((id) => !isNaN(id) && id !== null && id !== undefined)
    .sort()
    .join(",");

  const lastTagIds = lastTags
    .filter((id) => !isNaN(id) && id !== null && id !== undefined)
    .sort()
    .join(",");

  return currentTagIds !== lastTagIds;
};

const collectTagIds = (tagValues) => {
  const ids = [];
  if (Array.isArray(tagValues) && tagValues.length > 0) {
    ids.push(
      ...tagValues.map((tag) => parseInt(tag)).filter((id) => !isNaN(id))
    );
  } else if (tagValues) {
    const id = parseInt(tagValues);
    if (!isNaN(id)) ids.push(id);
  }
  return ids;
};

const createDraftListingDetails = (listingId = null) => {
  const tagIds = isEvalDataset.value
    ? [...getEvalTagIds(), ...collectTagIds(modality.value)]
    : [
        ...collectTagIds(domain.value),
        ...collectTagIds(modality.value),
        ...collectTagIds(useCase.value),
      ];

  const draftDetails = {
    name: title.value,
    description: description.value,
    short_description: shortDescription.value,
    delivery_method: "dataset",
    consumption_url: "https://example.com/dataset/123",
    preview_image: previewImage.value?.base64?.startsWith("data:image")
      ? previewImage.value.base64
      : null,
    price:
      (isEvalDataset.value || parseFloat(evalPrice.value) > 0) &&
      evalLicenseTypeStored.value
        ? 0
        : Number(price.value) || 0,
    minimum_bid: Number(minimumBid.value) || 0,
    currency: "USDC",
    data_license: license.value,
    receipt_address: recipientAddress.value,
    featured: Number(featured.value),
    accept_credits: acceptCredits.value,
    premium: premiumListingVisibility.value,
    dataset_id: selectedDataset.value ? Number(selectedDataset.value) : null,
    tags: tagIds,
    schema: formatSchema(),
    user_id: datasetInfo.value?.seller?.user_id,
    listing_id: listingId,
    payment_methods: paymentMethods.value,
    type:
      isEvalDataset.value || parseFloat(evalPrice.value) > 0
        ? "eval"
        : listingType.value,
  };

  const isEvalListing = isEvalDataset.value || parseFloat(evalPrice.value) > 0;
  if (isEvalListing) {
    draftDetails.is_open_source = evalLicenseTypeStored.value ?? false;
  }

  if (isEvalDataset.value && evalTagTypeSelected.value) {
    const tagTypeId = Number.parseInt(evalTagTypeSelected.value);
    if (!Number.isNaN(tagTypeId)) {
      draftDetails.tag_type_id = tagTypeId;
    }
  }

  return draftDetails;
};

const prepareDraftListingData = (listingID = null) => {
  const listingDetails = createDraftListingDetails(listingID);
  const payload = {
    listing: listingDetails,
  };
  return payload;
};

const toggleInfoToDraft = async () => {
  await setInfos(draftData.value);
  await formatLastDraftTime(draftData.value.updated_at);
  hasAutoSaveDraft.value = true;
  lastSavedData.value = JSON.parse(JSON.stringify(draftData.value));
  showHasDraftModal.value = false;
};

const cancelDraftListing = async () => {
  await deleteDraftListing(draftData.value.id);
  draftData.value = null;
  showHasDraftModal.value = false;
};

const handleSaveDraft = async () => {
  isSubmitting.value = true;

  const data = prepareDraftListingData();

  if (isDraft.value || hasAutoSaveDraft.value) {
    const currentListingId =
      listingId.value || localStorage.getItem("temp_editing_listing_id");
    if (currentListingId) {
      const result = await updateDraftListing(currentListingId, data);
      if (result) {
        lastSavedData.value = JSON.parse(JSON.stringify(data));
        await formatLastDraftTime(result.updated_at);
      }
    }
  } else {
    const currentListingId =
      isEditing.value && !isDraft.value ? listingId.value : null;
    const draftData = prepareDraftListingData(currentListingId);

    const newDraft = await createDraftListing(draftData);
    const draftId = newDraft?.id || newDraft?.data?.id;
    if (newDraft && draftId) {
      localStorage.setItem("temp_editing_listing_id", draftId.toString());
      localStorage.setItem("temp_editing_listing_is_draft", "true");
      hasAutoSaveDraft.value = true;
      lastSavedData.value = JSON.parse(JSON.stringify(draftData));
      await formatLastDraftTime(newDraft.updated_at);
    }
  }

  isSubmitting.value = false;
  router.back();
};

const lastDraftTime = ref(null);

const formatLastDraftTime = async (time) => {
  lastDraftTime.value = new Date(time).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getDraftButtonText = () => {
  const BaseText = isDraft.value ? "Updat" : "Sav";
  return isSubmitting.value
    ? `${BaseText}ing...`
    : `${BaseText}e Draft & Close`;
};

const clearErrors = () => {
  errors.value = {
    title: "",
    modality: "",
    description: "",
    shortDescription: "",
    schema: "",
    price: "",
    minimumBid: "",
    license: "",
    domain: "",
    recipientAddress: "",
    paymentMethods: "",
    previewImage: "",
    selectedDataset: "",
  };
};

const licenseData = ref(null);
const showLicenseModal = ref(false);
const showPreviewLicenseModal = ref(false);
const handleSaveLicense = (data) => {
  licenseData.value = data;
  showLicenseModal.value = false;
};

const handleStripeConnectModalClose = () => {
  showStripeConnectModal.value = false;
  stopStripeConnectionCheck();
};

const checkStripeConnection = () => {
  const updatedUserInfo = getUserInfo();
  const newUserStripeAccountId = updatedUserInfo?.seller?.stripe_account_id;

  // Only check user's own stripe account (for regular users)
  if (!isAdmin && newUserStripeAccountId && !userStripeAccountId.value) {
    saveUserInfo(updatedUserInfo);
    setTimeout(() => {
      showStripeConnectModal.value = false;
      stopStripeConnectionCheck();
      addToast("Stripe account connected successfully!", "success");
    }, 100);
  }
};

const startStripeConnectionCheck = () => {
  stopStripeConnectionCheck();

  checkStripeConnection();

  stripeConnectionCheckInterval.value = setInterval(() => {
    checkStripeConnection();
  }, 1000);

  window.addEventListener("focus", checkStripeConnection);

  window.addEventListener("storage", handleStorageChange);
};

const stopStripeConnectionCheck = () => {
  if (stripeConnectionCheckInterval.value) {
    clearInterval(stripeConnectionCheckInterval.value);
    stripeConnectionCheckInterval.value = null;
  }
  window.removeEventListener("focus", checkStripeConnection);
  window.removeEventListener("storage", handleStorageChange);
};

const handleStorageChange = (e) => {
  if (e.key === "userInfos") {
    checkStripeConnection();
  }
};

const handleWalletToggle = () => {
  walletPaymentEnabled.value = !walletPaymentEnabled.value;
  const walletIndex = paymentMethods.value.indexOf(
    availablePayments.value.find((p) => p.name === "wallet")?.id
  );
  if (walletPaymentEnabled.value) {
    if (walletIndex === -1) {
      paymentMethods.value.push(
        availablePayments.value.find((p) => p.name === "wallet")?.id
      );
    }
  } else if (walletIndex !== -1) {
    paymentMethods.value.splice(walletIndex, 1);
  }
};

const handleStripeToggle = () => {
  stripePaymentEnabled.value = !stripePaymentEnabled.value;
  const stripeIndex = paymentMethods.value.indexOf(
    availablePayments.value.find((p) => p.name === "stripe")?.id
  );
  if (stripePaymentEnabled.value) {
    if (stripeIndex === -1) {
      paymentMethods.value.push(
        availablePayments.value.find((p) => p.name === "stripe")?.id
      );
    }
  } else if (stripeIndex !== -1) {
    paymentMethods.value.splice(stripeIndex, 1);
  }
};

const handleAcceptCreditsChange = () => {
  const creditsIndex = paymentMethods.value.indexOf(
    availablePayments.value.find((p) => p.name === "credits")?.id
  );
  if (acceptCredits.value) {
    if (creditsIndex === -1) {
      paymentMethods.value.push(
        availablePayments.value.find((p) => p.name === "credits")?.id
      );
    }
  } else if (creditsIndex !== -1) {
    paymentMethods.value.splice(creditsIndex, 1);
  }
};

watch(isPublished, (newVal) => {
  if (!newVal) {
    clearErrors();
    missingFieldsModalData.value = [];
    showMissingFieldsModal.value = false;
  }
});

onMounted(async () => {
  availablePayments.value = await getActivePaymentMethods();
  try {
    const updatedUserInfo = getUserInfo();
    if (userStripeAccountId.value) {
      saveUserInfo(updatedUserInfo);
    }

    if (route.query.subpage === "new-eval") {
      isEvalDataset.value = true;
    }

    setIsFileProcessingValue(false);
    isLoading.value = true;
    hasPendingListingData.value = false;

    if (isDatasetFromRoute.value) {
      await loadTags();
      skipDatasetSchemaLoad.value = true;
      selectedDataset.value = route.params.datasetId;
      originalDatasetId.value = route.params.datasetId;
      isEvalDataset.value = route.query.isEvalDataset === "true";
      if (
        listingData.value?.datasets?.[0]?.dataset?.seller?.user?.id ||
        listingData.value?.datasets?.[0]?.dataset?.seller?.user_id
      ) {
        currentDatasetSellerId.value =
          listingData.value?.datasets?.[0]?.dataset?.seller?.user?.id ||
          listingData.value?.datasets?.[0]?.dataset?.seller?.user_id ||
          null;
      }
      await loadDatasetSchema(route.params.datasetId);
      checkForPendingListing();
    } else if (isEditing.value && listingId.value) {
      await loadTags();
      await loadListingData(listingId.value);
      await loadDatasets();
    } else {
      await Promise.all([loadDatasets(), loadTags()]);
    }

    if (isDraft.value) {
      hasAutoSaveDraft.value = true;
      const currentData = prepareDraftListingData();
      lastSavedData.value = structuredClone(currentData);
    }
    if (hasFlag("draft-feature")) {
      startAutoSave();
    }

    if (isEvalDataset.value) {
      await setEvalTagInputs();
    }
  } catch (error) {
    console.error("Error during component initialization", error);
    addToast("Error during component initialization", "error");
  } finally {
    isLoading.value = false;
  }
});

onUnmounted(() => {
  localStorage.removeItem("temp_editing_listing_is_draft");
  hasAutoSaveDraft.value = false;
  stopAutoSave();
  stopStripeConnectionCheck();
  if (route.name === "listing-edit") {
    localStorage.removeItem("temp_editing_listing_id");
  }
  // Clear file processing interval to prevent memory leaks
  if (fileProcessingInterval.value) {
    clearInterval(fileProcessingInterval.value);
    fileProcessingInterval.value = null;
  }
});

watch(stripeAccountId, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) {
    if (showStripeConnectModal.value) {
      showStripeConnectModal.value = false;
      stopStripeConnectionCheck();
    }
  }
});

watch(hasStripeAccount, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    if (showStripeConnectModal.value) {
      showStripeConnectModal.value = false;
      stopStripeConnectionCheck();
    }
  }
});

watch(acceptCredits, (newVal) => {
  handleAcceptCreditsChange();
});

watch(isEvalDataset, (newVal) => {
  if (!newVal) {
    evalTagTypeSelected.value = null;
    evalTagOptionSelected.value = null;
    evalTagOptions.value = [];
  }
});

watch(
  evalLicenseType,
  (newVal, oldVal) => {
    evalLicenseTypeStored.value = newVal === "open_source";

    if (isEvalDataset.value && newVal !== oldVal) {
      licenseData.value = null;
    }

    if (isEvalDataset.value && newVal === "open_source") {
      price.value = "0";
      evalPrice.value = "0";
    }
  },
  { immediate: true }
);

watch(showStripeConnectModal, (isOpen) => {
  if (isOpen) {
    const updatedUserInfo = getUserInfo();
    if (stripePaymentEnabled.value && userStripeAccountId.value) {
      saveUserInfo(updatedUserInfo);
      showStripeConnectModal.value = false;
      paymentMethods.value.push(
        availablePayments.value.find((p) => p.name === "stripe")?.id
      );
      addToast("Stripe account is already connected!", "success");
    }
  } else {
    stopStripeConnectionCheck();
  }
});
const evalTags = ref([]);
const evalTagTypesOptions = ref([]);
const evalTagTypeSelected = ref(null);
const evalTagOptions = ref([]);
const evalTagOptionSelected = ref(null);

const setEvalTagInputs = async () => {
  const tags = await fetchEvalTags();
  if (tags) {
    evalTags.value = tags;
    evalTagTypesOptions.value = buildEvalTagTypesOptions(tags);
    evalTagOptions.value = buildEvalTagOptions(null);
  }
};

const buildEvalTagTypesOptions = (evalTags) => {
  const options = evalTags.map((tag) => {
    return {
      label: tag.type.charAt(0).toUpperCase() + tag.type.slice(1),
      value: tag.id,
    };
  });
  return [{ label: "Select Industry", value: null }, ...options];
};

watch(evalTagTypeSelected, () => {
  evalTagOptions.value = buildEvalTagOptions(null);
});

watch(evalTagOptionSelected, async (newVal) => {
  const normalizedVal =
    newVal === "" || newVal === null || newVal === undefined ? null : newVal;

  if (normalizedVal === null) {
    return;
  }

  if (normalizedVal && !evalTagTypeSelected.value) {
    const matchingType = evalTags.value.find((evalTag) => {
      return evalTag.tags.some((tag) => {
        const tagIdNum = Number(tag.id);
        const selectedIdNum = Number(normalizedVal);
        return (
          tagIdNum === selectedIdNum || String(tag.id) === String(normalizedVal)
        );
      });
    });

    if (matchingType) {
      evalTagTypeSelected.value = matchingType.id;
      await nextTick();
      if (evalTagOptions.value.length === 0) {
        evalTagOptions.value = buildEvalTagOptions(matchingType.id);
      }
    }
  }

  if (evalTagOptionSelected.value === "") {
    evalTagOptionSelected.value = null;
  }
});

const buildEvalTagOptions = (evalTagType) => {
  const options = [];

  evalTags.value.forEach((evalTag) => {
    evalTag.tags.forEach((tag) => {
      options.push({ label: tag.name, value: tag.id });
    });
  });

  return [{ label: "Select Occupation", value: null }, ...options];
};

const getEvalTagIds = () => {
  const tagIds = [];

  if (evalTagOptionSelected.value) {
    const evalTagId = Number.parseInt(evalTagOptionSelected.value);
    if (!Number.isNaN(evalTagId)) {
      tagIds.push(evalTagId);
    }
  }

  return tagIds;
};

const findTagTypeIdFromListing = (listingData) => {
  if (!listingData.tag_type_id) {
    return null;
  }

  const tagTypeIdNum = Number(listingData.tag_type_id);
  const matchingType = evalTags.value.find(
    (evalTag) =>
      evalTag.id === tagTypeIdNum || Number(evalTag.id) === tagTypeIdNum
  );

  return matchingType ? matchingType.id : null;
};

const extractListingTagsArray = (listingData) => {
  return listingData.listing_tags || listingData.tags || [];
};

const findEvalTypeForTag = (tagId) => {
  const tagIdNum = Number(tagId);

  return evalTags.value.find((evalTag) => {
    return evalTag.tags.some((tag) => {
      const evalTagIdNum = Number(tag.id);
      return evalTagIdNum === tagIdNum || String(tag.id) === String(tagId);
    });
  });
};

const extractTagIdFromListingTag = (listingTag) => {
  if (typeof listingTag === "number") {
    return listingTag;
  }

  if (!listingTag || typeof listingTag !== "object") {
    return null;
  }

  if (listingTag.tag_type) {
    const tagType = listingTag.tag_type.type?.toLowerCase();
    if (tagType === "modality") {
      return null;
    }
  }

  return listingTag.id || listingTag.tag_id || null;
};

const findFirstRelevantTagInfo = (tagsArray, initialTagTypeId) => {
  let foundTagTypeId = initialTagTypeId;
  let foundTagId = null;

  for (const listingTag of tagsArray) {
    const tagId = extractTagIdFromListingTag(listingTag);

    if (!tagId) {
      continue;
    }

    const matchingType = findEvalTypeForTag(tagId);
    if (!matchingType) {
      continue;
    }

    foundTagId = tagId;
    if (!foundTagTypeId) {
      foundTagTypeId = matchingType.id;
    }
    break;
  }

  return { foundTagTypeId, foundTagId };
};

const selectTagOptionIfExists = (foundTagId) => {
  if (!foundTagId) {
    return;
  }

  const tagIdNum = Number(foundTagId);
  const matchingTag = evalTagOptions.value.find((option) => {
    const optionIdNum = Number(option.value);
    return (
      optionIdNum === tagIdNum || String(option.value) === String(foundTagId)
    );
  });

  if (matchingTag) {
    evalTagOptionSelected.value = matchingTag.value;
  } else if (tagIdNum && !Number.isNaN(tagIdNum)) {
    evalTagOptionSelected.value = tagIdNum;
  }
};

const applyFullTagSelection = async (foundTagTypeId, foundTagId) => {
  if (!foundTagTypeId && !foundTagId) {
    return;
  }

  if (!foundTagTypeId && foundTagId) {
    const matchingType = findEvalTypeForTag(foundTagId);
    if (!matchingType) {
      return;
    }
    foundTagTypeId = matchingType.id;
  }

  if (!foundTagTypeId) {
    return;
  }

  evalTagTypeSelected.value = foundTagTypeId;
  await nextTick();
  evalTagOptions.value = buildEvalTagOptions(null);
  selectTagOptionIfExists(foundTagId);
};

const applyTagTypeSelectionOnly = async (foundTagTypeId) => {
  await applyFullTagSelection(foundTagTypeId, null);
};

const loadEvalTagsFromListing = async (listingData) => {
  if (!isEvalDataset.value || !listingData) {
    return;
  }

  if (!evalTags.value.length) {
    await setEvalTagInputs();
  }

  let foundTagTypeId = findTagTypeIdFromListing(listingData);
  const tagsArray = extractListingTagsArray(listingData);

  if (!Array.isArray(tagsArray) || tagsArray.length === 0) {
    await applyTagTypeSelectionOnly(foundTagTypeId);
    return;
  }

  const { foundTagTypeId: resolvedTypeId, foundTagId } =
    findFirstRelevantTagInfo(tagsArray, foundTagTypeId);

  await applyFullTagSelection(resolvedTypeId, foundTagId);
};
</script>

<template>
  <Toast />
  <ImageCropperModal
    v-model="showCropper"
    :image-source="imageSource"
    @save="handleCroppedImageSave"
    @cancel="cancelCropping"
  />
  <FilePreviewModal
    v-model:show="showSchemaModal"
    :fileInfo="fileInfo"
    :mode="previewMode"
    @save="handleSaveSchema"
    @close="showSchemaModal = false"
  />

  <Alert
    :show="showEditAlert"
    :title="alertTitle"
    :message="alertMessage"
    :type="alertType"
    :showPasswordInput="alertShowPasswordInput"
    passwordLabel="Confirm update"
    passwordPlaceholder="Type your password to confirm"
    :passwordRequired="true"
    @confirm="confirmUpdateListing"
    @close="cancelAlert"
    :showCloseBtn="false"
    :isLoading="isSubmitting"
    loadingText="Updating listing..."
  />

  <Alert
    :show="showSellerChangeAlert && isAdmin"
    :title="alertTitle"
    :message="alertMessage"
    :type="alertType"
    confirmText="Continue"
    cancelText="Cancel"
    @confirm="confirmSellerChange"
    @close="cancelSellerChange"
    :showCloseBtn="false"
  />

  <Alert
    :show="showDeleteModal"
    :title="!isDraft ? 'Confirm Deletion' : 'Confirm Draft Deletion'"
    :message="deleteAlertMessage"
    :type="'danger'"
    :showPasswordInput="!isDraft"
    passwordLabel="Confirm deletion"
    :passwordPlaceholder="!isDraft ? 'Type your password to confirm' : ''"
    :passwordRequired="!isDraft"
    @confirm="confirmDeleteListing"
    @close="handleCloseDeleteModal"
    :showCloseBtn="false"
    :isLoading="isSubmitting"
    :loadingText="isDraft ? 'Deleting draft...' : 'Deleting listing...'"
  />

  <MissingFieldsModal
    :show="showMissingFieldsModal"
    :fields="missingFieldsModalData"
    @close="showMissingFieldsModal = false"
  />

  <Alert
    :show="showOpenSourceWarningAlert"
    title="Open Source – Dataset will be public"
    message="The dataset information will be made public on GitHub. This action cannot be reverted."
    type="warning"
    confirmText="Accept"
    cancelText="Cancel"
    :showCloseBtn="false"
    :confirmDisabled="!openSourceWarningCheckboxAccepted"
    @confirm="handleConfirmOpenSourceWarning"
    @close="handleCloseOpenSourceWarningAlert"
  >
    <template #custom>
      <label
        class="flex items-start gap-3 cursor-pointer mt-4 p-3 rounded-lg bg-gray-100 dark:bg-echoes-background-black/80 transition-colors"
      >
        <input
          type="checkbox"
          v-model="openSourceWarningCheckboxAccepted"
          class="mt-1 rounded border-gray-300 dark:border-echoes-border-light text-echoes-primary focus:ring-echoes-primary"
        />
        <span class="text-sm text-gray-600 dark:text-gray-300"
          >I understand that the dataset information will be public on GitHub
          and this action cannot be reverted.</span
        >
      </label>
    </template>
  </Alert>

  <ListingLicenseModal
    v-model:show="showLicenseModal"
    @save="handleSaveLicense"
    @close="showLicenseModal = false"
    :licenseData="licenseData"
    :isEvalDataset="isEvalDataset"
    :isOpenSource="evalLicenseTypeStored"
  />
  <ShowListingLicenseModal
    v-if="licenseData"
    v-model:show="showPreviewLicenseModal"
    @close="showPreviewLicenseModal = false"
    :licenseData="licenseData"
  />

  <!-- Stripe Connect Modal -->
  <Modal
    :show="showStripeConnectModal"
    :title="'Connect your Stripe account'"
    :closeOnClickOutside="true"
    :buttonText="'Close'"
    :modalSize="'normal'"
    :showCancelButton="false"
    @save="handleStripeConnectModalClose"
    @close="handleStripeConnectModalClose"
  >
    <template #description>
      Configure your account to receive payments securely
    </template>
    <template #default>
      <div class="py-4 flex flex-col items-center">
        <StripeButtonConnect
          @update:hasStripeAccount="startStripeConnectionCheck"
        />
        <div class="mt-4 text-center">
          <button
            @click="handleStripeConnectModalClose"
            class="underline text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
          >
            Skip for now
          </button>
        </div>
      </div>
    </template>
  </Modal>

  <Alert
    v-if="showHasDraftModal"
    :show="showHasDraftModal"
    title="You have a draft listing"
    message="Would you like to continue editing your draft listing?"
    type="info"
    confirmText="Continue"
    cancelText="Delete Draft"
    @confirm="toggleInfoToDraft"
    @close="cancelDraftListing"
    :showCloseBtn="false"
  />

  <div class="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-20">
    <div
      v-if="
        isEditing ||
        (!showListingForm && !route.params.datasetId) ||
        (!showListingForm &&
          !isEditing &&
          isEvalDataset &&
          route.params.datasetId)
      "
      class="breadcrumb"
    >
      <button
        @click="backRoute"
        class="breadcrumb-link"
        data-testid="back-to-listings-button"
      >
        <svg
          class="breadcrumb-icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
            clip-rule="evenodd"
          />
        </svg>
        <span>{{
          isEvalDataset && !isEditing ? "Back to Evals" : "Back to Listings"
        }}</span>
      </button>
    </div>

    <div v-if="showListingForm && !isEditing" class="breadcrumb">
      <button
        @click="handleBack()"
        class="breadcrumb-link"
        data-testid="back-to-listing-type-button"
      >
        <svg
          class="breadcrumb-icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
            clip-rule="evenodd"
          />
        </svg>
        <span>{{
          isEvalDataset ? "Back to Publishing Model" : "Back to Listing Type"
        }}</span>
      </button>
    </div>

    <div
      v-if="(route.params.datasetId || isEvalDataset) && !isEditing"
      class="flex flex-col items-center justify-center py-4 2xl:pb-8"
    >
      <Stepper :steps="steps" :currentStep="currentStep" />
    </div>

    <div class="mx-auto max-w-5xl 2xl:max-w-7xl">
      <h1
        v-if="!isLoading"
        class="text-2xl 2xl:text-3xl font-bold mb-3 bg-gradient-to-r from-[#FFFFFF] to-[#999999] bg-clip-text text-transparent w-fit"
        data-testid="listing-title"
      >
        {{ pageTitle }}
      </h1>
      <div v-else class="skeleton skeleton-title mb-3"></div>

      <p
        v-if="!isLoading && !isEditing && showListingForm"
        class="mb-12 text-sm 2xl:text-base text-white/80"
      >
        Now let's list the dataset you have uploaded to the Datalab. This will
        enable buyers to find it and purchase it.
      </p>

      <p
        v-if="!isLoading && !isEditing && !showListingForm && !isEvalDataset"
        class="mb-12 text-sm 2xl:text-base text-white/80"
      >
        Choose how you want to sell your dataset. You can set a fixed price,
        allow bidding, or both. Set your desired price and minimum bid to
        proceed with the listing creation.
      </p>
      <p
        v-if="!isLoading && !isEditing && !showListingForm && isEvalDataset"
        class="mb-12 text-sm 2xl:text-base text-white/80"
      >
        Select a publishing model for your eval. The listing form will update
        based on your choice.
      </p>
      <p
        v-if="!isLoading && isEditing && showListingForm"
        class="mb-12 text-sm 2xl:text-base text-white/80"
      >
        Update your listing information to make changes to how it appears in the
        Datalab.
      </p>

      <div
        v-if="!showListingForm && !isEditing && !isEvalDataset && !isLoading"
        class="space-y-6"
      >
        <ListingTypeSelection
          v-model="listingType"
          v-model:buyNowPrice="buyNowPrice"
          v-model:minimumBid="minimumBid"
          v-model:evalPrice="evalPrice"
          :isEvalDataset="isEvalDataset"
        />
        <div class="flex items-center justify-end">
          <button
            class="create-listing-btn hidden sm:block"
            data-testid="submit-pre-listing-button"
            @click="handleContinue"
            :disabled="
              !listingType ||
              (listingType !== 'bidding' && !buyNowPrice) ||
              (listingType !== 'buy_now' && !minimumBid)
            "
          >
            <span class="text-sm">Continue</span>
            <span class="tooltip" v-if="!listingType"
              >Select a listing type to continue</span
            >
            <span
              class="tooltip"
              v-else-if="listingType === 'buy_now' && !buyNowPrice"
              >Enter a buy now price to continue</span
            >
            <span
              class="tooltip"
              v-else-if="listingType === 'bidding' && !minimumBid"
              >Enter a minimum bid to continue</span
            >
            <span
              class="tooltip"
              v-else-if="
                listingType === 'buy_now_bidding' &&
                (!buyNowPrice || !minimumBid)
              "
              >Enter both buy now price and minimum bid to continue</span
            >
          </button>
        </div>
      </div>

      <div
        v-else-if="
          !showListingForm && !isEditing && isEvalDataset && !isLoading
        "
        class="space-y-6"
      >
        <EvalPublishingModelSelection
          v-model="evalDistributionMode"
          @continue="handleEvalPublishingContinue"
        />
      </div>

      <div v-else-if="showListingForm || isEditing" class="space-y-6">
        <!-- Skeleton Loading State -->
        <template v-if="isLoading">
          <div class="listing-form-row">
            <div class="listing-form-field">
              <div class="skeleton skeleton-label mb-2"></div>
              <div class="skeleton skeleton-input"></div>
            </div>
            <div class="listing-form-field">
              <div class="skeleton skeleton-label mb-2"></div>
              <div class="skeleton skeleton-input"></div>
            </div>
          </div>
          <div class="listing-form-row">
            <div class="listing-form-field">
              <div class="flex gap-4">
                <div class="w-1/2">
                  <div class="skeleton skeleton-label mb-2"></div>
                  <div class="skeleton skeleton-input"></div>
                </div>
                <div class="w-1/2">
                  <div class="skeleton skeleton-label mb-2"></div>
                  <div class="skeleton skeleton-input"></div>
                </div>
                <div class="w-1/2">
                  <div class="skeleton skeleton-label mb-2"></div>
                  <div class="skeleton skeleton-input"></div>
                </div>
              </div>
            </div>
            <div class="listing-form-field flex gap-4">
              <div class="w-1/2">
                <div class="skeleton skeleton-label mb-2"></div>
                <div class="skeleton skeleton-input"></div>
              </div>
              <div class="w-1/2">
                <div class="skeleton skeleton-label mb-2"></div>
                <div class="skeleton skeleton-input"></div>
              </div>
            </div>
          </div>
          <div class="listing-form-row">
            <div class="listing-form-field">
              <div class="skeleton skeleton-label mb-2"></div>
              <div class="skeleton skeleton-payment-methods"></div>
            </div>
            <div class="listing-form-field">
              <div class="skeleton skeleton-label mb-2"></div>
              <div class="skeleton skeleton-input"></div>
            </div>
          </div>
          <div class="listing-form-row">
            <div class="listing-form-field">
              <div class="skeleton skeleton-label mb-2"></div>
              <div class="skeleton skeleton-input"></div>
            </div>
            <div class="listing-form-field">
              <div class="skeleton skeleton-label mb-2"></div>
              <div class="skeleton skeleton-image-upload"></div>
            </div>
          </div>
          <div class="listing-form-row">
            <div class="listing-form-field flex-grow">
              <div class="skeleton skeleton-label mb-2"></div>
              <div class="skeleton skeleton-schema"></div>
            </div>
            <div class="listing-form-field">
              <div class="skeleton skeleton-label mb-2"></div>
              <div class="skeleton skeleton-textarea"></div>
            </div>
          </div>
          <div class="listing-form-field w-full">
            <div class="skeleton skeleton-label mb-2"></div>
            <div class="skeleton skeleton-textarea-large"></div>
          </div>
          <div class="action-container mb-6">
            <div class="skeleton skeleton-button"></div>
          </div>
        </template>

        <!-- Actual Form Content -->
        <template v-else>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div class="listing-form-field">
              <div class="flex items-center mb-2 w-fit gap-2">
                <label for="title" class="text-sm font-medium text-white/80">Title</label>
                <InputError :message="errors.title" class="text-xs text-red-500 m-0" />
              </div>
              <TextInput
                id="title"
                v-model="title"
                placeholder="Enter a title for your listing"
                dataTestId="listing-title-input"
                class="w-full"
                :required="true"
                :disabled="isSubmitting || isLoading"
              />
            </div>
            <div class="listing-form-field">
              <div class="flex items-center mb-2 w-fit gap-2">
                <label for="modality" class="text-sm font-medium text-white/80">Modality</label>
                <InputError :message="errors.modality" class="text-xs text-red-500 m-0" />
              </div>
              <Multiselect
                v-model="modality"
                :options="modalityOptions"
                :multiple="true"
                :close-on-select="false"
                dataTestId="listing-modality-select"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Select Modality Tags"
                class="w-full"
                @open="() => loadTags(true)"
                :disabled="isSubmitting || isLoading"
              />
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" v-if="isEvalDataset">
            <div class="listing-form-field">
              <div class="flex items-center mb-2 w-fit gap-2">
                <label for="evalPrice" class="text-sm font-medium text-white/80"
                  >Price per Eval</label
                >
                <InputError :message="errors.evalPrice" class="text-xs text-red-500 m-0" />
              </div>
              <TextInput
                id="evalPrice"
                v-model="evalPrice"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                class="w-full"
                :required="!evalLicenseTypeStored"
                :disabled="
                  !isEvalDataset ||
                  isSubmitting ||
                  isLoading ||
                  evalLicenseTypeStored
                "
              />
            </div>
            <div class="listing-form-field">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-white/80">
                  Configure your license.
                </span>
              </div>
              <div class="flex flex-wrap items-stretch gap-3">
                <button
                  @click="showLicenseModal = true"
                  v-if="!licenseData"
                  class="flex-1 min-w-0 h-11 text-sm border border-echoes-border-gray text-white rounded-[13px] px-4 transition-colors hover:border-white/20 items-center flex justify-center gap-2"
                >
                  Configure License
                  <i class="uil uil-arrow-circle-right text-lg"></i>
                </button>
                <div
                  v-else
                  class="flex-1 min-w-0 h-11 text-sm border border-echoes-border-gray bg-white/5 text-white rounded-[13px] px-4 items-center flex justify-between gap-3"
                >
                  <span class="flex items-center gap-2 min-w-0">
                    <i
                      class="uil uil-file-alt text-white/60 text-base shrink-0"
                    ></i>
                    <span class="truncate">{{ licenseData.licenseName }}</span>
                  </span>
                  <div class="flex gap-1.5 shrink-0">
                    <button
                      type="button"
                      class="text-xs text-white/80 hover:text-white hover:bg-white/10 rounded-lg px-2.5 py-1 transition-colors"
                      @click="showPreviewLicenseModal = true"
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      class="text-xs text-white/80 hover:text-white hover:bg-white/10 rounded-lg px-2.5 py-1 transition-colors"
                      @click="showLicenseModal = true"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div class="w-full flex gap-4 mb-2 md:mb-0">
              <div :class="isEvalDataset ? 'w-1/2' : 'w-1/2'">
                <div class="flex items-center mb-2 w-fit gap-2">
                  <label for="price" class="text-sm font-medium text-white/80"
                    >{{ isEvalDataset ? "Core Dataset Price" : "Price"
                    }}<span class="text-gray-400 ml-1" v-if="!isEvalDataset"
                      >(in USD)</span
                    ></label
                  >
                  <InputError :message="errors.price" class="text-xs text-red-500 m-0" />
                </div>
                <TextInput
                  id="price"
                  v-model="price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  dataTestId="listing-price-input"
                  class="w-full"
                  :required="
                    (listingType !== 'bidding' && !isEvalDataset) ||
                    (isCoreDatasetFieldsEnabled && !evalLicenseTypeStored)
                  "
                  :disabled="
                    listingType === 'bidding' ||
                    isSubmitting ||
                    isLoading ||
                    (isEvalDataset && !isCoreDatasetFieldsEnabled) ||
                    isFileInfoEmpty ||
                    (isEvalDataset && evalLicenseTypeStored)
                  "
                />
              </div>
              <div :class="isEvalDataset ? 'w-1/2' : 'w-1/2'">
                <div class="flex items-center mb-2 w-fit gap-2">
                  <label for="minimumBid" class="text-sm font-medium text-white/80">{{
                    isEvalDataset ? "Core Dataset Minimum Bid" : "Minimum Bid"
                  }}</label>
                  <InputError
                    :message="errors.minimumBid"
                    class="text-xs text-red-500 m-0"
                  />
                </div>
                <TextInput
                  id="minimumBid"
                  v-model="minimumBid"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  dataTestId="listing-minimum-bid-input"
                  class="w-full"
                  :required="
                    (listingType !== 'buy_now' && !isEvalDataset) ||
                    isCoreDatasetFieldsEnabled
                  "
                  :disabled="
                    listingType === 'buy_now' ||
                    isSubmitting ||
                    isLoading ||
                    (isEvalDataset && !isCoreDatasetFieldsEnabled) ||
                    isFileInfoEmpty
                  "
                />
              </div>
            </div>
            <div class="mb-2 md:mb-0 flex gap-4" v-if="!isEvalDataset">
              <div class="w-1/2">
                <div class="flex items-center mb-2 w-fit gap-2">
                  <label for="domain" class="text-sm font-medium text-white/80"
                    >Domain<span class="text-white/80 ml-1"
                      >(Optional)</span
                    ></label
                  >
                </div>
                <Multiselect
                  v-model="domain"
                  :options="domainOptions"
                  :multiple="true"
                  :close-on-select="false"
                  dataTestId="listing-domain-select"
                  :clear-on-select="false"
                  :preserve-search="true"
                  placeholder="Select Domain Tags"
                  class="w-full"
                  @open="() => loadTags(true)"
                  :disabled="isSubmitting || isLoading"
                />
              </div>
              <div class="w-1/2">
                <div class="flex items-center mb-2 w-fit gap-2">
                  <label for="useCase" class="text-sm font-medium text-white/80"
                    >Use Case<span class="text-white/80 ml-1"
                      >(Optional)</span
                    ></label
                  >
                </div>
                <Multiselect
                  v-model="useCase"
                  :options="useCaseOptions"
                  :multiple="true"
                  :close-on-select="false"
                  dataTestId="listing-use-case-select"
                  :clear-on-select="false"
                  :preserve-search="true"
                  placeholder="Select Use Case Tags"
                  class="w-full"
                  @open="() => loadTags(true)"
                  :disabled="isSubmitting || isLoading"
                />
              </div>
            </div>
            <div v-if="isEvalDataset" class="grid grid-cols-1 gap-4">
              <div>
                <label
                  for="evalTagOptionSelected"
                  class="block text-white/80 text-sm font-medium mb-2 flex items-center gap-2"
                >
                  <div>Occupation (Optional)</div>
                  <div
                    class="text-white/80 hover:text-white/60 transition-colors relative group"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      class="w-5 h-5 cursor-help flex-shrink-0"
                    >
                      <path
                        fill="currentColor"
                        d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z"
                      ></path>
                    </svg>
                    <span class="eval-tooltip">
                      Choose the occupation that typically performs tasks like
                      this. Mapping evals to jobs (based on U.S. SOC titles)
                      helps everyone see where AI is strong or weak in
                      real-world work so we can plan training, tooling, and
                      guardrails effectively.
                    </span>
                  </div>
                </label>

                <SelectInput
                  :id="`evalTagOptionSelected`"
                  v-model="evalTagOptionSelected"
                  :options="evalTagOptions"
                  :multiple="false"
                  :close-on-select="false"
                  :placeholder="`Select Occupation Tag`"
                  class="w-full"
                  :disabled="isSubmitting || isLoading"
                  :clearable="true"
                />
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div class="listing-form-field">
              <div class="flex items-center mb-2 w-fit gap-2">
                <label for="paymentMethods" class="text-sm font-medium text-white/80"
                  >Payment Methods</label
                >
                <InputError
                  :message="errors.paymentMethods"
                  class="text-xs text-red-500 m-0"
                />
              </div>
              <div class="payment-methods-container">
                <!-- Stripe Payment Toggle. -->
                <div
                  class="payment-method-item"
                  v-if="hasFlag('stripe-payment')"
                >
                  <div
                    class="payment-method-toggle flex items-center justify-between gap-2"
                  >
                    <div v-if="!isAdminEdit" class="flex items-center gap-3">
                      <button
                        @click="handleStripeToggle"
                        :disabled="
                          isSubmitting ||
                          isLoading ||
                          (isEvalDataset && evalLicenseTypeStored)
                        "
                        :class="[
                          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none',
                          stripePaymentEnabled
                            ? 'bg-gradient-to-r from-green-500 to-green-700'
                            : 'bg-echoes-background-gray opacity-60',
                        ]"
                      >
                        <span
                          :class="[
                            'inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out',
                            stripePaymentEnabled
                              ? 'translate-x-6'
                              : 'translate-x-1',
                          ]"
                        />
                      </button>
                      <div
                        class="payment-method-content"
                        :class="stripePaymentEnabled ? '' : 'opacity-60'"
                      >
                        <span class="payment-method-name text-sm">Stripe</span>
                        <div class="payment-method-icon">
                          <img :src="StripeLogo" alt="Stripe" class="w-6 h-6" />
                        </div>
                      </div>
                    </div>
                    <div v-else class="flex items-center gap-3">
                      <button
                        @click="handleStripeToggle"
                        :disabled="
                          isSubmitting ||
                          isLoading ||
                          (isEvalDataset && evalLicenseTypeStored)
                        "
                        :class="[
                          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none',
                          stripePaymentEnabled &&
                          currentDatasetSellerInfo?.stripe_account_id
                            ? 'bg-gradient-to-r from-green-500 to-green-700'
                            : 'bg-echoes-background-gray',
                          !currentDatasetSellerInfo?.stripe_account_id &&
                          isAdminEdit
                            ? 'cursor-not-allowed'
                            : '',
                        ]"
                      >
                        <span
                          :class="[
                            'inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out',
                            stripePaymentEnabled &&
                            currentDatasetSellerInfo?.stripe_account_id
                              ? 'translate-x-6'
                              : 'translate-x-1',
                          ]"
                        />
                      </button>
                      <div
                        class="payment-method-content"
                        :class="
                          stripePaymentEnabled &&
                          currentDatasetSellerInfo?.stripe_account_id
                            ? ''
                            : 'opacity-60'
                        "
                      >
                        <span class="payment-method-name text-sm">Stripe</span>
                        <div class="payment-method-icon">
                          <img :src="StripeLogo" alt="Stripe" class="w-6 h-6" />
                        </div>
                      </div>
                    </div>
                    <div class="flex flex-col flex-1 max-w-md">
                      <!-- Display Stripe account ID for admin (dataset owner's stripe account) -->
                      <div
                        v-if="
                          stripePaymentEnabled && stripeAccountId && isAdmin
                        "
                        class="stripe-account-label w-full px-4 py-2.5 h-11 text-sm rounded-[13px] border border-echoes-border-gray bg-echoes-background-black text-white flex items-center"
                        data-testid="listing-stripe-account-id-input"
                      >
                        <span class="text-white truncate">
                          {{ stripeAccountId }}
                        </span>
                      </div>

                      <!-- Display Stripe account ID for regular user (their own stripe account) -->
                      <div
                        v-else-if="
                          stripePaymentEnabled &&
                          userStripeAccountId &&
                          !isAdmin
                        "
                        class="stripe-account-label w-full px-4 py-2.5 h-11 text-sm rounded-[13px] border border-echoes-border-gray bg-echoes-background-black text-white flex items-center"
                        data-testid="listing-stripe-account-id-input"
                      >
                        <span class="text-white truncate">
                          {{ userStripeAccountId }}
                        </span>
                      </div>

                      <!-- Prompt to onboard if Stripe is enabled but regular user not connected -->
                      <div
                        v-else-if="
                          stripePaymentEnabled &&
                          !userStripeAccountId &&
                          !isAdminEdit
                        "
                        @click="
                          !isSubmitting && !isLoading
                            ? (showStripeConnectModal = true)
                            : null
                        "
                        class="stripe-account-label w-full px-4 py-2.5 h-11 text-sm rounded-[13px] border border-echoes-border-gray bg-echoes-background-black text-white/80 hover:bg-echoes-background-black hover:border-white/20 hover:text-white focus:outline-none transition-all duration-200 flex items-center justify-between cursor-pointer gap-2"
                        :class="
                          isSubmitting || isLoading
                            ? 'opacity-60 cursor-not-allowed'
                            : ''
                        "
                        data-testid="listing-stripe-account-id-input"
                      >
                        <span class="text-white/50">
                          Click here to onboard to Stripe (2 min)
                        </span>
                        <i
                          class="uil uil-arrow-circle-right text-white/50 text-lg"
                        ></i>
                      </div>

                      <!-- Placeholder when Stripe is disabled -->
                      <div
                        v-else-if="stripePaymentEnabled && !isAdminEdit"
                        class="stripe-account-label w-full px-4 py-2.5 h-11 text-sm rounded-[13px] border border-echoes-border-gray bg-echoes-background-black text-white/50 flex items-center opacity-60"
                        data-testid="listing-stripe-account-id-input"
                      >
                        <span>Enable Stripe to connect account</span>
                      </div>

                      <div
                        v-else
                        class="stripe-account-label w-full px-4 py-2.5 h-11 text-sm rounded-[13px] border border-echoes-border-gray bg-echoes-background-black text-white flex items-center opacity-60 cursor-not-allowed"
                        data-testid="listing-stripe-account-id-input"
                      >
                        <span class="text-white truncate"></span>
                      </div>

                      <InputError
                        :message="errors.stripeAccountId"
                        class="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <!-- Wallet Payment Toggle -->
                <div class="payment-method-item">
                  <div
                    class="payment-method-toggle flex items-center justify-between gap-2"
                  >
                    <div class="flex items-center gap-3">
                      <button
                        @click="handleWalletToggle"
                        :disabled="
                          isSubmitting ||
                          isLoading ||
                          (isEvalDataset && evalLicenseTypeStored)
                        "
                        :class="[
                          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none',
                          walletPaymentEnabled
                            ? 'bg-gradient-to-r from-green-500 to-green-700'
                            : 'bg-echoes-background-gray opacity-60',
                        ]"
                      >
                        <span
                          :class="[
                            'inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out',
                            walletPaymentEnabled
                              ? 'translate-x-6'
                              : 'translate-x-1',
                          ]"
                        />
                      </button>
                      <div
                        class="payment-method-content"
                        :class="walletPaymentEnabled ? '' : 'opacity-60'"
                      >
                        <span class="payment-method-name text-sm">USDC</span>
                        <div class="payment-method-icon">
                          <img :src="USDCLogo" alt="USDC" class="w-6 h-6" />
                        </div>
                      </div>
                    </div>
                    <div class="flex flex-col flex-1 max-w-md">
                      <TextInput
                        id="recipient"
                        v-model="recipientAddress"
                        dataTestId="listing-recipient-address-input"
                        placeholder="Enter your wallet address on Base to accept USDC payments (0x123...)"
                        class="w-full"
                        :required="
                          !(isEvalDataset && evalLicenseTypeStored) &&
                          walletPaymentEnabled
                        "
                        :disabled="
                          isSubmitting ||
                          isLoading ||
                          !walletPaymentEnabled ||
                          (isEvalDataset && evalLicenseTypeStored)
                        "
                        :class="walletPaymentEnabled ? '' : 'opacity-60'"
                      />
                      <InputError
                        :message="errors.recipientAddress"
                        class="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="mb-2 md:mb-0 flex flex-col h-full space-y-4"
            >
              <div class="flex-grow flex-1 min-h-0 flex flex-col">
                <div class="flex items-center mb-2 w-fit gap-2 shrink-0">
                  <label for="datasetSelect" class="text-sm font-medium text-white/80"
                    >Core Dataset</label
                  >
                  <InputError :message="errors.schema" class="text-xs text-red-500 m-0" />
                  <InputError
                    :message="errors.selectedDataset"
                    class="text-xs text-red-500 m-0"
                  />
                </div>
                <div
                  class="flex flex-col w-full flex-1 min-h-0"
                >
                  <div class="w-full mb-2 z-10 shrink-0">
                    <SelectInput
                      id="datasetSelect"
                      v-model="selectedDataset"
                      :options="availableDatasets"
                      dataTestId="listing-dataset-select"
                      placeholder="Select a dataset"
                      class="w-full"
                      :disabled="
                        isSubmitting || isLoading || isDatasetSelectionDisabled
                      "
                      @open="() => loadDatasets(true)"
                    />
                  </div>
                  <div
                    id="datasetSelect"
                    :class="[
                      'flex-1 min-h-0 flex flex-col',
                      isFileProcessing
                        ? ' min-h-[80px] relative border border-echoes-border-gray rounded-[13px] bg-echoes-background-black flex flex-col justify-center items-center px-3 sm:px-6'
                        : 'min-h-[80px] relative border border-echoes-border-gray rounded-[13px] bg-echoes-background-black flex flex-col justify-center items-center px-3 sm:px-6',
                    ]"
                  >
                    <div v-if="selectedDataset" class="flex items-center w-full py-3">
                      <div class="mr-2 sm:mr-4 text-echoes-primary flex-shrink-0">
                        <div
                          v-if="isFileProcessing && !isOnlyEvalDataset"
                          class="animate-spin"
                        >
                          <svg
                            class="w-10 h-10 2xl:w-12 2xl:h-12 text-violet-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            ></circle>
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        </div>
                        <svg
                          v-else
                          class="w-10 h-10 2xl:w-12 2xl:h-12 flex-shrink-0 text-echoes-primary"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path
                            d="M2 4C2 2.89543 2.89543 2 4 2H14L20 8V20C20 21.1046 19.1046 22 18 22H4C2.89543 22 2 21.1046 2 20V4Z"
                            fill="currentColor"
                            fill-opacity="0.2"
                          />
                          <path
                            d="M14 2L20 8H15C14.4477 8 14 7.55228 14 7V2Z"
                            fill="currentColor"
                          />
                          <path
                            d="M13 11L16 14M16 14L13 17M16 14H10"
                            stroke="currentColor"
                            stroke-width="1"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            fill="none"
                            v-if="isExternalDataset"
                          />
                        </svg>
                      </div>
                      <div
                        class="flex ml-1 items-center sm:justify-between w-full flex-wrap sm:flex-nowrap gap-2"
                        v-if="!isExternalDataset && !isOnlyEvalDataset"
                      >
                        <div class="flex flex-col flex-grow min-w-0">
                          <p class="font-medium text-white/80 text-sm 2xl:text-base truncate max-w-[200px] md:max-w-[250px]">
                            {{ fileInfo.name || "Dataset Schema" }}
                          </p>
                          <p class="text-white/40 text-xs">
                            {{
                              currentDatasetSellerInfo?.user?.username ||
                              currentDatasetSellerInfo?.username ||
                              listingData?.datasets?.[0]?.dataset?.seller?.user
                                ?.username ||
                              listingData?.datasets?.[0]?.dataset?.seller
                                ?.username ||
                              "Unknown Seller"
                            }}
                          </p>
                          <div class="flex">
                            <p class="text-xs text-white/80">{{ fileInfo.size }}</p>
                            <span class="text-xs font-bold text-white/80 mx-1" v-if="fileInfo.size"
                              >•</span
                            >
                            <p class="text-xs text-white/80">
                              {{
                                fileInfo.schema
                                  ? `${fileInfo.schema.length} fields`
                                  : "No schema available"
                              }}
                            </p>
                          </div>
                        </div>
                        <div class="flex gap-2 items-center w-full sm:w-auto">
                          <div class="group relative">
                            <button
                              @click="
                                () => {
                                  previewMode = 'preview';
                                  showSchemaModal = true;
                                }
                              "
                              class="preview-button"
                              data-testid="listing-schema-preview-btn"
                              :disabled="
                                isSubmitting || isLoading || isFileProcessing
                              "
                            >
                              Preview
                            </button>
                          </div>
                          <div class="group relative">
                            <button
                              @click="
                                () => {
                                  previewMode = 'edit';
                                  showSchemaModal = true;
                                }
                              "
                              class="edit-button"
                              data-testid="listing-schema-edit-btn"
                              :disabled="
                                isSubmitting || isLoading || isFileProcessing
                              "
                            >
                              <div
                                v-if="isFileProcessing"
                                class="text-white/80 hover:text-echoes-primary transition-colors relative group cursor-pointer"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  id="info-circle"
                                  class="w-4 h-4"
                                >
                                  <path
                                    fill="currentColor"
                                    d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z"
                                  ></path>
                                </svg>
                                <span class="tooltip"
                                  >Available after schema processing is
                                  complete. You will receive a
                                  notification.</span
                                >
                              </div>
                              Edit
                            </button>
                          </div>
                        </div>
                      </div>
                      <div v-else-if="isOnlyEvalDataset">
                        <p class="font-medium text-white/80 text-sm 2xl:text-base truncate max-w-[200px] md:max-w-[250px]">Only Eval files</p>
                      </div>
                      <div v-else>
                        <p class="font-medium text-white/80 text-sm 2xl:text-base truncate max-w-[200px] md:max-w-[250px]">External Dataset</p>
                      </div>
                    </div>
                    <div v-else class="schema-empty-state flex-1 min-h-0">
                      <p class="schema-empty-message">
                        Select dataset to view and edit schema
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" v-if="isEvalDataset">
            <div class="listing-form-field">
              <div class="flex items-center mb-2 w-fit gap-2">
                <label for="previewImage" class="text-sm font-medium text-white/80"
                  >Preview Image</label
                >
                <InputError
                  :message="errors.previewImage"
                  class="text-xs text-red-500 m-0"
                />
              </div>
              <div class="flex">
                <div class="flex items-center gap-2 w-full">
                  <button
                    @click="$refs.imageInput.click()"
                    class="upload-btn"
                    data-testid="listing-preview-image-upload-btn"
                    :class="{ 'upload-btn-selected': previewImage }"
                    :disabled="isSubmitting || isLoading"
                  >
                    <svg
                      class="w-6 h-6"
                      :class="previewImage ? 'text-green-400' : 'text-white/80'"
                      viewBox="0 0 94 94"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15.6667 58.7501L15.6667 66.5834C15.6667 73.0728 20.9274 78.3334 27.4167 78.3334L66.5834 78.3334C73.0728 78.3334 78.3334 73.0728 78.3334 66.5834V58.7501M47.0001 15.6667L47.0001 62.6667M47.0001 15.6667L62.6667 31.3334M47.0001 15.6667L31.3334 31.3334"
                        stroke="currentColor"
                        stroke-width="6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <span v-if="!previewImage">Upload</span>
                    <span v-else class="flex items-center">
                      <span
                        class="truncate max-w-[120px] inline-block overflow-hidden"
                        >{{ previewImage.name }}</span
                      >
                      <button
                        @click.stop="removeSelectedImage"
                        class="ml-2 p-1 rounded-full"
                        title="Remove image"
                        data-testid="listing-preview-image-remove-btn"
                      >
                        <svg
                          class="w-2.5 h-2.5 text-white/80 hover:text-red-500 transition-all duration-200 ease-in-out"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 14 14"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                          />
                        </svg>
                      </button>
                    </span>
                  </button>
                  <input
                    ref="imageInput"
                    type="file"
                    id="imageInput"
                    @change="handleImageUpload"
                    data-testid="listing-preview-image-input"
                    class="hidden"
                    accept="image/*"
                  />
                  <span class="text-white/80 text-sm my-1 sm:my-0">or</span>
                  <button
                    @click="generatePreviewImage"
                    class="generate-btn"
                    data-testid="listing-preview-image-generate-btn"
                    :disabled="isSubmitting || isLoading || true"
                  >
                    <svg
                      class="w-6 h-6 text-white/80"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-width="2"
                        d="M8.737 8.737a21.49 21.49 0 0 1 3.308-2.724m0 0c3.063-2.026 5.99-2.641 7.331-1.3 1.827 1.828.026 6.591-4.023 10.64-4.049 4.049-8.812 5.85-10.64 4.023-1.33-1.33-.736-4.218 1.249-7.253m6.083-6.11c-3.063-2.026-5.99-2.641-7.331-1.3-1.827 1.828-.026 6.591 4.023 10.64m3.308-9.34a21.497 21.497 0 0 1 3.308 2.724m2.775 3.386c1.985 3.035 2.579 5.923 1.248 7.253-1.336 1.337-4.245.732-7.295-1.275M14 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z"
                      />
                    </svg>
                    Generate
                    <span class="tooltip">Coming soon</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" v-if="!isEvalDataset">
            <div class="listing-form-field">
              <div class="flex items-center mb-2 w-fit gap-2">
                <label for="license" class="text-sm font-medium text-white/80">License</label>
              </div>
              <button
                @click="showLicenseModal = true"
                v-if="!licenseData"
                class="w-full h-11 text-sm border border-echoes-border-gray text-white rounded-[13px] px-4 transition-colors hover:border-white/20 items-center flex justify-center gap-2"
              >
                Configure License
                <i class="uil uil-arrow-circle-right text-lg"></i>
              </button>
              <div
                v-else
                class="w-full min-h-11 min-w-0 text-sm border border-echoes-border-gray bg-white/5 text-white rounded-[13px] px-4 py-2 items-center flex justify-between gap-3 overflow-hidden"
              >
                <span
                  class="flex items-center gap-2 min-w-0 flex-1 overflow-hidden"
                >
                  <i
                    class="uil uil-file-alt text-white/60 text-base shrink-0"
                  ></i>
                  <span class="license-card-in-grid__name truncate">{{
                    licenseData.licenseName
                  }}</span>
                </span>
                <div class="flex gap-1.5 shrink-0">
                  <button
                    type="button"
                    class="text-xs text-white/80 hover:text-white hover:bg-white/10 rounded-lg px-2.5 py-1 transition-colors"
                    @click="showPreviewLicenseModal = true"
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    class="text-xs text-white/80 hover:text-white hover:bg-white/10 rounded-lg px-2.5 py-1 transition-colors"
                    @click="showLicenseModal = true"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
            <div class="listing-form-field">
              <div class="flex items-center mb-2 w-fit gap-2">
                <label for="previewImage" class="text-sm font-medium text-white/80"
                  >Preview Image</label
                >
                <InputError
                  :message="errors.previewImage"
                  class="text-xs text-red-500 m-0"
                />
              </div>
              <div class="flex">
                <div class="flex items-center gap-2 w-full">
                  <button
                    @click="$refs.imageInput.click()"
                    class="upload-btn"
                    data-testid="listing-preview-image-upload-btn"
                    :class="{ 'upload-btn-selected': previewImage }"
                    :disabled="isSubmitting || isLoading"
                  >
                    <svg
                      class="w-6 h-6"
                      :class="previewImage ? 'text-green-400' : 'text-white/80'"
                      viewBox="0 0 94 94"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15.6667 58.7501L15.6667 66.5834C15.6667 73.0728 20.9274 78.3334 27.4167 78.3334L66.5834 78.3334C73.0728 78.3334 78.3334 73.0728 78.3334 66.5834V58.7501M47.0001 15.6667L47.0001 62.6667M47.0001 15.6667L62.6667 31.3334M47.0001 15.6667L31.3334 31.3334"
                        stroke="currentColor"
                        stroke-width="6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <span v-if="!previewImage">Upload</span>
                    <span v-else class="flex items-center">
                      <span
                        class="truncate max-w-[120px] inline-block overflow-hidden"
                        >{{ previewImage.name }}</span
                      >
                      <button
                        @click.stop="removeSelectedImage"
                        class="ml-2 p-1 rounded-full"
                        title="Remove image"
                        data-testid="listing-preview-image-remove-btn"
                      >
                        <svg
                          class="w-2.5 h-2.5 text-white/80 hover:text-red-500 transition-all duration-200 ease-in-out"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 14 14"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                          />
                        </svg>
                      </button>
                    </span>
                  </button>
                  <input
                    ref="imageInput"
                    type="file"
                    id="imageInput"
                    @change="handleImageUpload"
                    data-testid="listing-preview-image-input"
                    class="hidden"
                    accept="image/*"
                  />
                  <span class="text-white/80 text-sm my-1 sm:my-0">or</span>
                  <button
                    @click="generatePreviewImage"
                    class="generate-btn"
                    data-testid="listing-preview-image-generate-btn"
                    :disabled="isSubmitting || isLoading || true"
                  >
                    <svg
                      class="w-6 h-6 text-white/80"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-width="2"
                        d="M8.737 8.737a21.49 21.49 0 0 1 3.308-2.724m0 0c3.063-2.026 5.99-2.641 7.331-1.3 1.827 1.828.026 6.591-4.023 10.64-4.049 4.049-8.812 5.85-10.64 4.023-1.33-1.33-.736-4.218 1.249-7.253m6.083-6.11c-3.063-2.026-5.99-2.641-7.331-1.3-1.827 1.828-.026 6.591 4.023 10.64m3.308-9.34a21.497 21.497 0 0 1 3.308 2.724m2.775 3.386c1.985 3.035 2.579 5.923 1.248 7.253-1.336 1.337-4.245.732-7.295-1.275M14 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z"
                      />
                    </svg>
                    Generate
                    <span class="tooltip">Coming soon</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="w-full" v-if="!isEvalDataset">
            <div class="flex items-center mb-2 w-fit gap-2">
              <label for="shortDescription" class="text-sm font-medium text-white/80"
                >Short Description</label
              >
              <InputError
                :message="errors.shortDescription"
                class="text-xs text-red-500 m-0"
              />
              <span class="text-xs text-white/80 ml-auto mr-1"
                >{{ shortDescription.length }}/300</span
              >
            </div>
            <textarea
              class="text-area__field h-[132px]"
              v-model="shortDescription"
              placeholder="Add a short description for your listing (max 300 characters)"
              maxlength="300"
              data-testid="listing-short-description-input"
            />
          </div>

          <div class="w-full" v-if="isEvalDataset">
            <div class="flex items-center mb-2 w-fit gap-2">
              <label for="shortDescription" class="text-sm font-medium text-white/80"
                >Short Description</label
              >
              <InputError
                :message="errors.shortDescription"
                class="text-xs text-red-500 m-0"
              />
              <span class="text-xs text-white/80 ml-auto mr-1"
                >{{ shortDescription.length }}/300</span
              >
            </div>

            <textarea
              class="text-area__field h-[132px]"
              v-model="shortDescription"
              placeholder="Add a short description for your listing (max 300 characters)"
              maxlength="300"
              data-testid="listing-short-description-input"
            />
          </div>

          <div class="mb-2 md:mb-0 w-full">
            <div class="flex items-center mb-2 w-fit gap-2">
              <label for="description" class="text-sm font-medium text-white/80">Description</label>
              <InputError :message="errors.description" class="text-xs text-red-500 m-0" />
            </div>
            <MarkdownRenderer
              v-model="description"
              dataTestId="listing-description-input"
              placeholder="Use markdown to format your description"
              :rowsLength="10"
              :is-editing="isEditing"
              :disabled="isSubmitting || isLoading"
              @paste="handlePaste"
            />
          </div>

          <div
            class="flex flex-col items-center justify-end mb-6 border-2 border-echoes-primary p-4 rounded-lg gap-2 bg-echoes-primary/5"
            v-if="isAdmin"
          >
            <div class="text-echoes-primary font-semibold text-start w-full">
              Admin Actions
            </div>
            <div class="flex gap-8 w-full justify-between">
              <div class="w-1/4">
                <div class="flex items-center mb-2 w-fit gap-2">
                  <label for="price" class="text-sm font-medium text-white/80"
                    >Listing Fee<span class="text-gray-400 ml-1"
                      >(%)</span
                    ></label
                  >
                  <InputError :message="errors.fee" class="text-xs text-red-500 m-0" />
                </div>
                <TextInput
                  id="fee"
                  v-model="listingFee"
                  type="number"
                  min="0"
                  step="0.01"
                  :max="99.99"
                  placeholder="0.00"
                  dataTestId="listing-fee-input"
                  class="w-full"
                  :disabled="isSubmitting || isLoading"
                />
              </div>
              <div class="flex gap-8 w-full flex justify-end">
                <div class="flex items-center gap-2">
                  <Checkbox
                    v-model:checked="premiumListingVisibility"
                    id="premium-listing-checkbox"
                    dataTestId="listing-premium-listing-checkbox"
                  />
                  <label
                    class="text-sm text-white/80"
                    for="premium-listing-checkbox"
                    >Premium Listing</label
                  >
                  <div
                    class="text-white/80 hover:text-echoes-primary transition-colors relative group"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      id="info-circle"
                      class="w-4 h-4"
                    >
                      <path
                        fill="currentColor"
                        d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z"
                      ></path>
                    </svg>
                    <span class="tooltip"
                      >Show Premium listings options for this listing.</span
                    >
                  </div>
                </div>

                <div class="flex items-center gap-2">
                  <Checkbox
                    v-model:checked="featured"
                    id="featured-checkbox"
                    dataTestId="listing-featured-checkbox"
                  />
                  <label class="text-sm text-white/80" for="featured-checkbox"
                    >Featured</label
                  >
                  <div
                    class="text-white/80 hover:text-echoes-primary transition-colors relative group"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      id="info-circle"
                      class="w-4 h-4"
                    >
                      <path
                        fill="currentColor"
                        d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z"
                      ></path>
                    </svg>
                    <span class="tooltip"
                      >Featured listings receive special visibility and are
                      highlighted in the Datalab.</span
                    >
                  </div>
                </div>

                <div class="flex items-center gap-2">
                  <Checkbox
                    v-model:checked="acceptCredits"
                    id="accept-credits-checkbox"
                    dataTestId="listing-accept-credits-checkbox"
                    :disabled="
                      isSubmitting ||
                      isLoading ||
                      (isEvalDataset && evalLicenseTypeStored)
                    "
                  />
                  <label
                    class="text-sm text-white/80"
                    for="accept-credits-checkbox"
                    >Accept Credits</label
                  >
                  <div
                    class="text-white hover:text-echoes-primary transition-colors relative group"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      id="info-circle"
                      class="w-4 h-4"
                    >
                      <path
                        fill="currentColor"
                        d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z"
                      ></path>
                    </svg>
                    <span class="tooltip"
                      >Accept user credits for this listing.</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Premium Listing Options -->
          <div
            v-if="isAdmin && premiumListingVisibility"
            class="mb-2 md:mb-0 w-full"
          >
            <div class="premium-dropdown-container">
              <!-- Premium Dropdown Header -->
              <div
                @click="togglePremiumDropdown"
                class="premium-dropdown-header"
                :class="{ active: showPremiumDropdown }"
              >
                <div class="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-5 h-5 text-[#ffcc65]"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    />
                  </svg>
                  <label
                    class="form-label font-semibold dark:text-white text-[15px] cursor-pointer"
                    for="premium-options"
                    >Premium Listing Options</label
                  >
                  <span
                    v-if="selectedPremiumOptions.length > 0"
                    class="px-2 py-0.5 bg-[#ffcc65]/20 text-[#ffcc65] text-xs rounded-full border border-[#ffcc65]/40"
                  >
                    {{ selectedPremiumOptions.length }} selected
                  </span>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-5 h-5 text-white/60 transition-transform duration-200"
                  :class="{ 'rotate-180': showPremiumDropdown }"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              <!-- Premium Dropdown Content -->
              <div
                class="premium-dropdown-content"
                :class="{ open: showPremiumDropdown }"
              >
                <p class="text-sm text-white/60 mb-4">
                  Select premium features to enhance your listing visibility and
                  performance
                </p>

                <!-- Premium Features Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div
                    v-for="option in premiumOptions"
                    :key="option.value"
                    @click="togglePremiumOption(option.value)"
                    class="premium-option-card"
                    :class="{
                      selected: selectedPremiumOptions.includes(option.value),
                    }"
                  >
                    <div class="flex items-start justify-between">
                      <div class="flex-1">
                        <div class="flex items-center gap-2 mb-1">
                          <div
                            class="premium-checkbox"
                            :class="{
                              checked: selectedPremiumOptions.includes(
                                option.value
                              ),
                            }"
                          >
                            <svg
                              v-if="
                                selectedPremiumOptions.includes(option.value)
                              "
                              xmlns="http://www.w3.org/2000/svg"
                              class="w-3 h-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clip-rule="evenodd"
                              />
                            </svg>
                          </div>
                          <h4 class="font-semibold text-white text-sm">
                            {{ option.label }}
                          </h4>
                        </div>
                        <p class="text-xs text-white/60">
                          {{ option.description }}
                        </p>
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-4 h-4 text-[#ffcc65] flex-shrink-0 ml-2"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <!-- Selected Premium Options Summary -->
                <div
                  v-if="selectedPremiumOptions.length > 0"
                  class="mt-4 p-3 rounded-lg bg-[#ffcc65]/10 border border-[#ffcc65]/30"
                >
                  <h5 class="text-sm font-semibold text-[#ffcc65] mb-2">
                    Selected Premium Features
                  </h5>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="selectedValue in selectedPremiumOptions"
                      :key="selectedValue"
                      class="px-2 py-1 bg-[#ffcc65]/20 text-[#ffcc65] text-xs rounded-full border border-[#ffcc65]/40"
                    >
                      {{
                        premiumOptions.find(
                          (opt) => opt.value === selectedValue
                        )?.label
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Container -->
          <div class="flex items-center justify-end mb-6">
            <div
              class="flex gap-8 w-full"
              :class="isEditing || isDraft ? 'justify-between' : 'justify-end'"
            >
              <div
                v-if="isEditing || isDraft"
                class="text-sm text-red-600 font-semibold underline underline-offset-4 cursor-pointer items-center flex"
                @click="handleShowDeleteModal"
                data-testid="listing-delete-btn"
              >
                {{ isDraft ? "Delete Draft" : "Delete Listing" }}
              </div>
              <div class="flex items-center gap-2">
                <!-- Pending Listing Status -->
                <div
                  v-if="hasPendingListingData && !isEditing && isPublished"
                  class="flex items-center gap-2 text-yellow-400 text-sm"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    class="w-5 h-5"
                  >
                    <path
                      fill="currentColor"
                      d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z"
                    ></path>
                  </svg>
                  <span
                    >Listing saved - will publish when processing
                    completes</span
                  >
                </div>

                <div class="flex items-center gap-3 me-5">
                  <div
                    class="text-sm font-bold text-gray-900 dark:text-gray-300 flex items-center gap-1"
                  >
                    Draft
                    <div
                      class="text-white hover:text-echoes-primary transition-colors relative group"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        id="info-circle"
                        class="w-4 h-4"
                      >
                        <path
                          fill="currentColor"
                          d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z"
                        ></path>
                      </svg>
                      <span class="tooltip"
                        >Only you will be able to see it.</span
                      >
                    </div>
                  </div>
                  <div
                    class="inline-flex items-center cursor-pointer"
                    @click="isPublished = !isPublished"
                  >
                    <input
                      type="checkbox"
                      v-model="isPublished"
                      class="sr-only peer"
                    />
                    <div
                      data-testid="listing-published-checkbox-switch"
                      class="relative w-20 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 peer-checked:after:translate-x-[58px] rtl:peer-checked:after:-translate-x-[56px] peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600 dark:peer-checked:bg-green-600"
                    ></div>
                  </div>
                  <div
                    class="text-sm font-bold text-gray-900 dark:text-gray-300 flex items-center gap-1"
                  >
                    {{ requiresPublicationReview ? "Review" : "Live" }}
                    <div
                      class="text-white hover:text-echoes-primary transition-colors relative group"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        id="info-circle"
                        class="w-4 h-4"
                      >
                        <path
                          fill="currentColor"
                          d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z"
                        ></path>
                      </svg>
                      <span class="tooltip">{{
                        requiresPublicationReview
                          ? "Goes to admin review before becoming publicly visible."
                          : "Publicly visible on the Datalab."
                      }}</span>
                    </div>
                  </div>
                </div>

                <button
                  v-if="hasFlag('draft-feature')"
                  class="save-draft-btn hidden sm:block"
                  @click="handleSaveDraft"
                  :disabled="isSubmitting || isLoading"
                  data-testid="listing-save-draft-btn"
                >
                  {{ getDraftButtonText() }}
                </button>

                <button
                  class="create-listing-btn hidden sm:block"
                  @click="submitCreateListing"
                  :disabled="isSubmitting || isLoading"
                  data-testid="create-listing-btn"
                >
                  <ButtonLoading
                    v-if="isSubmitting"
                    :text="isEditing ? 'Updating...' : 'Creating...'"
                    size="sm"
                  />
                  <span v-else class="text-sm">{{ buttonText }}</span>
                  <span v-if="isFileProcessing" class="tooltip"
                    >Waiting for file processing to complete...</span
                  >
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>

.preview-button,
.edit-button {
    @apply flex flex-row items-center gap-2 px-5 py-1.5 text-white rounded-md text-sm transition-colors flex-1 sm:flex-initial;

    &:disabled {
        @apply opacity-50 cursor-not-allowed;
    }
}

.preview-button {
    @apply bg-echoes-background-gray hover:bg-white/10 py-1.5 px-3 text-xs lg:py-1.5 lg:px-3.5 lg:text-sm;

    &:disabled {
        @apply hover:bg-echoes-background-gray cursor-not-allowed;
    }
}

.edit-button {
    @apply bg-echoes-background-gray hover:bg-echoes-background-black py-1.5 px-3 text-xs lg:py-1.5 lg:px-3.5 lg:text-sm;

    &:disabled {
        @apply hover:bg-echoes-background-gray cursor-not-allowed;
    }
}

.upload-btn,
.generate-btn {
    @apply w-full sm:flex-1 h-11 flex items-center gap-2 justify-center px-3 sm:px-5 py-2 text-sm rounded-[13px] bg-echoes-background-black text-white/80 border border-echoes-border-gray placeholder-white/50 transition-all duration-200 ease-in-out;

    &:disabled {
        @apply opacity-50 cursor-not-allowed;
    }
}

.upload-btn {
    @apply bg-echoes-background-black text-white hover:bg-echoes-background-black;

    &:disabled {
        @apply hover:bg-echoes-background-black;
    }
}

.upload-btn-selected {
    @apply border border-green-500 bg-echoes-background-gray dark:border-green-400;

    &:disabled {
        @apply opacity-50 cursor-not-allowed;
    }
}

.generate-btn {
    @apply bg-echoes-background-black text-white/80;
    position: relative;
}

.generate-btn:disabled {
    @apply opacity-60 cursor-not-allowed;
}

.tooltip {
    @apply absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 text-xs font-semibold text-white bg-echoes-background-gray rounded-lg whitespace-normal w-max max-w-[350px];
    visibility: hidden;
    opacity: 0;
    transition: all 0.2s ease-in-out;
    z-index: 100;
}

.group:hover .tooltip {
    visibility: visible;
    opacity: 1;
    z-index: 100;
}

.create-listing-btn {
    @apply px-6 2xl:px-8 py-2 text-sm 2xl:text-base bg-gradient-to-br from-green-500 to-green-700 text-white font-semibold rounded-full hover:bg-gradient-to-br hover:from-green-600 hover:to-green-800 transition duration-200 flex items-center justify-center min-w-[160px] disabled:opacity-70 disabled:cursor-not-allowed;
    position: relative;
}

.save-draft-btn {
    @apply px-6 2xl:px-8 py-2 text-sm 2xl:text-base bg-gradient-to-br from-gray-500 to-gray-700 text-white font-semibold rounded-full hover:bg-gradient-to-br hover:from-gray-600 hover:to-gray-800 transition duration-200 flex items-center justify-center min-w-[160px] disabled:opacity-70 disabled:cursor-not-allowed;
    position: relative;
}

.schema-empty-state {
    @apply flex items-center justify-center w-full min-h-0 py-3;
}

.schema-empty-message {
    @apply text-white/80 text-sm;
}

.breadcrumb {
    @apply my-6 w-fit;
}

.breadcrumb-link {
    @apply flex underline items-center gap-1 text-white/80 hover:text-echoes-primary transition-colors duration-200 text-sm;
}

.breadcrumb-icon {
    @apply w-4 h-4;
}

.btn {
    @apply px-4 py-2 rounded-lg font-medium transition-colors;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.text-area__field {

        @apply w-full h-full text-sm px-4 py-3 bg-echoes-background-black text-white border border-echoes-border-gray rounded-[13px] placeholder-white/70 focus:outline-none  focus:border-echoes-primary resize-none transition-all duration-200;

        &::placeholder {
            @apply transition-all duration-200 text-white/50 hover:text-white/80;
        }

        &:hover {
            @apply bg-echoes-background-black
            border-white/20
            text-white
            ;
        }

        &:focus::placeholder {
            @apply opacity-0 translate-x-1;
        }

        &:focus {
            @apply border-echoes-primary;
        }

        &:disabled {
            @apply cursor-not-allowed opacity-60 bg-echoes-background-gray/30;
        }
    }


.back-button {
        @apply flex items-center gap-2 text-white/80 hover:text-echoes-primary transition-colors text-sm;
}

.premium-dropdown-container {
    @apply w-full;
}

.premium-dropdown-header {
    @apply flex items-center justify-between p-4 rounded-lg border transition-all duration-200 cursor-pointer;
    background: rgba(255, 204, 101, 0.05);
    border-color: rgba(255, 204, 101, 0.2);
}

.premium-dropdown-header:hover {
    background: rgba(255, 204, 101, 0.1);
    border-color: rgba(255, 204, 101, 0.4);
}

.premium-dropdown-header.active {
    background: rgba(255, 204, 101, 0.1);
    border-color: #ffcc65;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}

.premium-dropdown-content {
    @apply overflow-hidden transition-all duration-300 ease-in-out border border-t-0 rounded-b-lg;
    background: rgba(255, 204, 101, 0.02);
    border-color: rgba(255, 204, 101, 0.2);
    max-height: 0;
    opacity: 0;
}

.premium-dropdown-content.open {
    @apply p-4;
    max-height: 1000px;
    opacity: 1;
    border-color: #ffcc65;
}

.premium-option-card {
    @apply p-4 rounded-lg border transition-all duration-200 cursor-pointer;
    background: rgba(255, 204, 101, 0.05);
    border-color: rgba(255, 204, 101, 0.2);
}

.premium-option-card:hover {
    background: rgba(255, 204, 101, 0.1);
    border-color: rgba(255, 204, 101, 0.4);
    transform: translateY(-1px);
}

.premium-option-card.selected {
    background: rgba(255, 204, 101, 0.15);
    border-color: #ffcc65;
    box-shadow: 0 4px 12px rgba(255, 204, 101, 0.2);
}

.premium-checkbox {
    @apply w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200;
    border-color: rgba(255, 204, 101, 0.4);
    background: transparent;
}

.premium-checkbox.checked {
    background: #ffcc65;
    border-color: #ffcc65;
    color: #000;
}

.format-notice {
    @apply text-sm mb-3 bg-blue-500/20 border border-blue-500/50 text-blue-500 rounded-[13px] text-center break-words whitespace-normal;
    max-width: 100%;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.check-icon-container {
    @apply text-sm truncate;
    z-index: 10;
}
.payment-methods-container {
    @apply space-y-4;
}

.payment-method-item {
    @apply border border-echoes-border-gray rounded-xl p-4 bg-echoes-background-black/50;
}

.payment-method-toggle {
    @apply flex items-center;
}

.payment-method-label {
    @apply flex items-center cursor-pointer;
}


.payment-method-content {
    @apply flex items-center space-x-3;
}

.payment-method-icon {
    @apply flex items-center justify-center;
}

.payment-method-icon img {
    @apply object-contain;
}

.payment-method-name {
    @apply font-semibold text-white/80 text-sm 2xl:text-base;
}

.payment-method-details {
    @apply mt-3 ml-8;
}

.eval-tooltip {
    @apply absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 text-xs text-gray-100 bg-echoes-background-gray rounded-lg whitespace-normal w-max max-w-[300px] border-0;
    visibility: hidden;
    opacity: 0;
    transition: all 0.2s ease-in-out;
    z-index: 50;
    pointer-events: none;
}

.group:hover .eval-tooltip {
    visibility: visible;
    opacity: 1;
}

/* Skeleton Loading Styles */
.skeleton {
    @apply bg-echoes-background-gray rounded-[13px];
    background: linear-gradient(90deg,
        rgba(255, 255, 255, 0.05) 0%,
        rgba(255, 255, 255, 0.1) 50%,
        rgba(255, 255, 255, 0.05) 100%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s ease-in-out infinite;
}

@keyframes skeleton-loading {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

.skeleton-title {
    @apply h-8 w-64;
}

.skeleton-text {
    @apply h-4 w-full max-w-2xl;
}

.skeleton-label {
    @apply h-4 w-24;
}

.skeleton-input {
    @apply h-11 w-full;
}

.skeleton-textarea {
    @apply h-[132px] w-full;
}

.skeleton-textarea-large {
    @apply h-48 w-full;
}

.skeleton-button {
    @apply h-10 w-40;
}

.skeleton-payment-methods {
    @apply h-32 w-full;
}

.skeleton-image-upload {
    @apply h-11 w-full;
}

.skeleton-schema {
    @apply h-32 w-full;
}
</style>
